// ============================================================
// aiCoachEngine.js
// نسخه‌ی نهایی v4.0 — موتور مشاور هوشمند جامع، role-aware، self-healing
// ============================================================
// تغییرات نسبت به v3:
//   • Timezone محلی (رفع drift UTC/Local)
//   • Unified Data Reader (رفع drift بین plan و snapshot)
//   • Role-aware: student / teacher / parent / admin
//   • ۵۰+ نوع پیشنهاد (شامل teacher/parent/admin)
//   • Self-healing در ۲۰ نقطه
//   • CACHE_VERSION = 4
//   • همه‌ی مسیرها اصلاح‌شده
// ============================================================

import { COURSE_DETAILS } from '../pages/CourseDetail';
import {
  normalizeGradeId as curriculumNormalizeGrade,
  getSubjectIdsFor,
  isHighSchool as curriculumIsHighSchool,
  needsField as curriculumNeedsField,
  normalizeFieldId as curriculumNormalizeField,
} from '../core/education/curriculum.js';

// ============================================================
// SECTION 0 — CONSTANTS
// ============================================================

export const CACHE_VERSION = 4;

export const USER_STATES = {
  // Student
  NEW_VISITOR: 'NEW_VISITOR',
  ONBOARDING_1: 'ONBOARDING_1',
  ONBOARDING_2: 'ONBOARDING_2',
  ONBOARDING_3: 'ONBOARDING_3',
  RETURNING: 'RETURNING',
  LOST: 'LOST',
  BURNT_OUT: 'BURNT_OUT',
  STRUGGLING: 'STRUGGLING',
  BUILDING: 'BUILDING',
  STEADY: 'STEADY',
  EXCELLING: 'EXCELLING',
  EXAM_WEEK: 'EXAM_WEEK',
  DEADLINE_PRESSURE: 'DEADLINE_PRESSURE',
  PLATEAU: 'PLATEAU',
  // Teacher
  TEACHER_NEW: 'TEACHER_NEW',
  TEACHER_ACTIVE: 'TEACHER_ACTIVE',
  TEACHER_TEAM_BUILDER: 'TEACHER_TEAM_BUILDER',
  TEACHER_EXAM_MAKER: 'TEACHER_EXAM_MAKER',
  // Parent
  PARENT_NEW: 'PARENT_NEW',
  PARENT_ACTIVE: 'PARENT_ACTIVE',
  // Admin
  ADMIN_NEW: 'ADMIN_NEW',
  ADMIN_ACTIVE: 'ADMIN_ACTIVE',
};

export const ADVISOR_MODES = {
  GUIDE: 'GUIDE',
  NURTURER: 'NURTURER',
  RECOVERER: 'RECOVERER',
  PROTECTOR: 'PROTECTOR',
  SUPPORTER: 'SUPPORTER',
  COACH: 'COACH',
  PARTNER: 'PARTNER',
  CHALLENGER: 'CHALLENGER',
  STRATEGIST: 'STRATEGIST',
  CRISIS_MANAGER: 'CRISIS_MANAGER',
  CHEERLEADER: 'CHEERLEADER',
  TEACHER_COACH: 'TEACHER_COACH',
  TEACHER_GUIDE: 'TEACHER_GUIDE',
  PARENT_GUIDE: 'PARENT_GUIDE',
  PARENT_SUPPORTER: 'PARENT_SUPPORTER',
  ADMIN_COACH: 'ADMIN_COACH',
};

export const SUGGESTION_TYPES = {
  // آنبوردینگ
  PICK_FIRST_BOOK: 'pick_first_book',
  WATCH_FIRST_VIDEO: 'watch_first_video',
  COMPLETE_FIRST_LESSON: 'complete_first_lesson',
  TAKE_FIRST_QUIZ: 'take_first_quiz',
  SET_GRADE: 'set_grade',
  MEET_THE_APP: 'meet_the_app',
  CLAIM_WELCOME: 'claim_welcome',
  // آموزشی
  STUDY: 'study',
  REVIEW: 'review',
  FINISH_LESSON: 'finish_lesson',
  WATCH_VIDEO: 'watch_video',
  READ_TEXTBOOK: 'read_textbook',
  DO_EXERCISE: 'do_exercise',
  EXPLORE: 'explore',
  ADVANCE_TOPIC: 'advance_topic',
  // آزمون
  EXAM: 'exam',
  CHALLENGE: 'challenge',
  RESUME_DRAFT: 'resume_draft',
  REVIEW_MISTAKES: 'review_mistakes',
  WAIT_CORRECTION: 'wait_correction',
  // برنامه‌ریزی
  FOLLOW_PLAN: 'follow_plan',
  CREATE_PLAN: 'create_plan',
  RESCHEDULE: 'reschedule',
  SET_GOAL: 'set_goal',
  // انگیزشی
  STREAK_CHECK: 'streak_check',
  VIEW_MEDALS: 'view_medals',
  CELEBRATE: 'celebrate',
  ALMOST_MEDAL: 'almost_medal',
  // اجتماعی
  LEADERBOARD: 'leaderboard',
  CHALLENGE_FRIEND: 'challenge_friend',
  JOIN_GROUP: 'join_group',
  MENTOR_FRIEND: 'mentor_friend',
  // خودمراقبتی
  TAKE_BREAK: 'take_break',
  SLEEP_REMINDER: 'sleep_reminder',
  MOOD_CHECK: 'mood_check',
  // رشدی
  PARENT_VIEW: 'parent_view',
  LEARN_SKILL: 'learn_skill',
  // سیستمی
  FIX_STREAK: 'fix_streak',
  RESUME_YESTERDAY: 'resume_yesterday',
  WELCOME_BACK: 'welcome_back',
  RESTART_JOURNEY: 'restart_journey',
  // معلم
  CREATE_TEAM: 'create_team',
  CREATE_EXAM: 'create_exam',
  VIEW_TEAM_ACTIVITY: 'view_team_activity',
  PUBLISH_PROJECT: 'publish_project',
  RESPOND_REQUESTS: 'respond_requests',
  // والدین
  VIEW_CHILD_PROGRESS: 'view_child_progress',
  VIEW_CHILD_EXAMS: 'view_child_exams',
  VIEW_CHILD_TEAM: 'view_child_team',
  SET_CHILD_PROFILE: 'set_child_profile',
  // ادمین
  REVIEW_PLATFORM: 'review_platform',
  REVIEW_USERS: 'review_users',
};

// آستانه‌های DRAGONS — هماهنگ با MedalsPage.jsx (منبع واحد)
const DRAGONS_THRESHOLDS = [0, 10, 100, 500, 1000, 2000, 3500, 6000, 10000];

const STATE_TO_MODE = {
  NEW_VISITOR: ADVISOR_MODES.GUIDE,
  ONBOARDING_1: ADVISOR_MODES.GUIDE,
  ONBOARDING_2: ADVISOR_MODES.NURTURER,
  ONBOARDING_3: ADVISOR_MODES.NURTURER,
  RETURNING: ADVISOR_MODES.RECOVERER,
  LOST: ADVISOR_MODES.RECOVERER,
  BURNT_OUT: ADVISOR_MODES.PROTECTOR,
  STRUGGLING: ADVISOR_MODES.SUPPORTER,
  BUILDING: ADVISOR_MODES.COACH,
  STEADY: ADVISOR_MODES.PARTNER,
  EXCELLING: ADVISOR_MODES.CHALLENGER,
  EXAM_WEEK: ADVISOR_MODES.STRATEGIST,
  DEADLINE_PRESSURE: ADVISOR_MODES.CRISIS_MANAGER,
  PLATEAU: ADVISOR_MODES.CHEERLEADER,
  TEACHER_NEW: ADVISOR_MODES.TEACHER_GUIDE,
  TEACHER_ACTIVE: ADVISOR_MODES.TEACHER_COACH,
  TEACHER_TEAM_BUILDER: ADVISOR_MODES.TEACHER_COACH,
  TEACHER_EXAM_MAKER: ADVISOR_MODES.TEACHER_COACH,
  PARENT_NEW: ADVISOR_MODES.PARENT_GUIDE,
  PARENT_ACTIVE: ADVISOR_MODES.PARENT_SUPPORTER,
  ADMIN_NEW: ADVISOR_MODES.ADMIN_COACH,
  ADMIN_ACTIVE: ADVISOR_MODES.ADMIN_COACH,
};

const MODE_BUDGET = {
  GUIDE: 3,
  NURTURER: 4,
  RECOVERER: 4,
  PROTECTOR: 3,
  SUPPORTER: 5,
  COACH: 5,
  PARTNER: 5,
  CHALLENGER: 6,
  STRATEGIST: 5,
  CRISIS_MANAGER: 4,
  CHEERLEADER: 5,
  TEACHER_COACH: 4,
  TEACHER_GUIDE: 3,
  PARENT_GUIDE: 3,
  PARENT_SUPPORTER: 4,
  ADMIN_COACH: 3,
};

const MAIN_SUBJECTS = [
  'riazi', 'olum', 'farsi', 'ghoran', 'hedye', 'motaleat',
  'payam', 'farhang', 'arabi', 'english',
  'din',
  'hendese', 'hesaban', 'fizik', 'shimi', 'amar_ehtemal', 'riazi_gosaste',
  'zist',
  'jamee', 'tarikh', 'joghrafia', 'eqtesad', 'manteq',
  'ravanshenasi', 'falsafe', 'olon_fonon', 'riazi_amar',
  'salamat', 'hoviat', 'joghrafia_iran', 'tarikh_moaser',
];

const FARSI_DAY_NAMES = ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنج‌شنبه', 'جمعه'];

// انواع اطلاعاتی (برای MIN_WAIT)
export const INFORMATIONAL_TYPES = new Set([
  'wait_correction',
  'sleep_reminder',
  'meet_the_app',
  'claim_welcome',
  'view_medals',
  'celebrate',
]);

// انواع حذف‌شده (برای cleanup pending)
const REMOVED_TYPES = new Set(['daily_spin']);

// ============================================================
// SECTION 1 — UTILITIES
// ============================================================

const toFa = (n) => String(n ?? '').replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]);
const pad2 = (n) => String(n).padStart(2, '0');
const safeArray = (v) => (Array.isArray(v) ? v : []);
const safeObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const safeNumber = (v, fb = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fb;
};
const safeString = (v, fb = '') => (typeof v === 'string' ? v : fb);
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
const avg = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0);
const sum = (arr) => arr.reduce((a, b) => a + b, 0);
const stddev = (arr) => {
  if (arr.length < 2) return 0;
  const m = avg(arr);
  return Math.sqrt(avg(arr.map((x) => (x - m) ** 2)));
};
const idsEqual = (a, b) => {
  if (a == null || b == null) return false;
  return String(a) === String(b);
};

const safeJSON = (raw, fallback) => {
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch { return fallback; }
};
const readLS = (key, fallback = null) => {
  try { return safeJSON(localStorage.getItem(key), fallback); }
  catch { return fallback; }
};

// ============================================================
// SECTION 1.5 — TIME UTILITIES (local timezone)
// ============================================================

const localTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const localDateKey = (offset = 0) => {
  const d = new Date(Date.now() - offset * 86400000);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const parseAnyDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }
  const s = String(input).trim();
  if (!s) return null;
  // ISO
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }
  // Local YMD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, day] = s.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }
  // Persian digits YMD
  if (/^[۰-۹]{4}-[۰-۹]{2}-[۰-۹]{2}$/.test(s)) {
    const en = s.replace(/[۰-۹]/g, (c) => '0123456789'['۰۱۲۳۴۵۶۷۸۹'.indexOf(c)]);
    const [y, m, day] = en.split('-').map(Number);
    const d = new Date(y, m - 1, day);
    return isNaN(d.getTime()) ? null : d;
  }
  // Fallback
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const daysAgo = (input) => {
  const d = parseAnyDate(input);
  if (!d) return 999;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const persianDayIndex = () => (new Date().getDay() + 1) % 7;

const dayName = () => FARSI_DAY_NAMES[persianDayIndex()] || '';

// ============================================================
// SECTION 2 — NORMALIZERS
// ============================================================

const normalizeGrade = (grade) => {
  try { return curriculumNormalizeGrade(grade); } catch { return grade || null; }
};
const normalizeField = (field) => {
  try { return curriculumNormalizeField(field); } catch { return field || null; }
};
const normalizeRole = (role) => {
  const r = String(role || 'student').toLowerCase();
  if (['student', 'teacher', 'parent', 'admin'].includes(r)) return r;
  return 'student';
};

const mergeDaily = (localMap, serverMap) => {
  const out = {};
  const keys = new Set([...Object.keys(safeObject(localMap)), ...Object.keys(safeObject(serverMap))]);
  for (const k of keys) {
    out[k] = Math.max(Number(localMap?.[k]) || 0, Number(serverMap?.[k]) || 0);
  }
  return out;
};

const mergeBookStats = (localMap, serverMap) => {
  const out = {};
  const keys = new Set([...Object.keys(safeObject(localMap)), ...Object.keys(safeObject(serverMap))]);
  for (const k of keys) {
    out[k] = Math.max(Number(localMap?.[k]) || 0, Number(serverMap?.[k]) || 0);
  }
  return out;
};

const extractLatestPlan = (plans) => {
  const arr = safeArray(plans).filter((p) => p && typeof p === 'object');
  if (arr.length === 0) return null;
  return [...arr].sort((a, b) => {
    const ta = parseAnyDate(a?.createdAt)?.getTime() || 0;
    const tb = parseAnyDate(b?.createdAt)?.getTime() || 0;
    return tb - ta;
  })[0];
};

const computeStreak = (user, userId, daily) => {
  const serverStreak = Number(user?.streak) || 0;
  const serverLast = user?.lastActivityDate || null;

  const legacy = safeObject(readLS(`streak_${userId}`, {}));
  const legacyStreak = Number(legacy.streak) || 0;
  const legacyLast = legacy.lastDate || legacy.lastActivityDate || null;

  const arena = safeObject(readLS(`streak-${userId}`, {}));
  const arenaStreak = Number(arena.streak) || 0;
  const arenaLast = arena.lastActivityDate || null;

  const dailyDates = Object.entries(safeObject(daily))
    .filter(([_, v]) => Number(v) > 0)
    .map(([k]) => k)
    .sort();
  const dailyLatest = dailyDates[dailyDates.length - 1] || null;

  const candidates = [
    { s: serverStreak, d: serverLast },
    { s: legacyStreak, d: legacyLast },
    { s: arenaStreak, d: arenaLast },
  ].filter((c) => c.d && daysAgo(c.d) <= 2);

  const best = candidates.sort((a, b) => b.s - a.s)[0];

  const streak = best?.s || 0;
  const streakLastDate = best?.d || dailyLatest || null;
  const lastActiveDays = streakLastDate ? daysAgo(streakLastDate) : 999;

  return { streak, streakLastDate, lastActiveDays };
};

const computeAchievements = (user, userId, xp) => {
  const xpNum = Number(xp) || 0;
  let unlockedCount = 0;
  let almostCount = 0;
  const almostDragons = [];
  for (let i = 1; i < DRAGONS_THRESHOLDS.length; i++) {
    const t = DRAGONS_THRESHOLDS[i];
    if (xpNum >= t) unlockedCount++;
    else if (xpNum >= t * 0.75) {
      almostCount++;
      almostDragons.push({ index: i, threshold: t, progress: Math.round((xpNum / t) * 100) });
    }
  }
  const celebrated = new Set([
    ...safeArray(user?.celebratedDragons),
    ...safeArray(readLS(`celebratedDragons_${userId}`, [])),
  ]);
  unlockedCount = Math.max(unlockedCount, celebrated.size);
  return { unlockedCount, almostCount, almostDragons };
};

// ============================================================
// SECTION 3 — UNIFIED DATA READER
// ============================================================

const readUnifiedData = (userId, user, exams) => {
  if (!userId || userId === 'unknown') return null;

  const u = safeObject(user);
  const safeExams = safeArray(exams);
  const role = normalizeRole(u.role);
  const grade = normalizeGrade(u.grade || u.class || readLS(`user-grade-${userId}`));
  const field = normalizeField(u.field || readLS(`user-field-${userId}`));

  // ===== DAILY =====
  const dailyLocal = safeObject(readLS(`dailyProgress_${userId}`, {}));
  const dailyServer = safeObject(u.dailyProgress);
  const daily = mergeDaily(dailyLocal, dailyServer);

  // ===== BOOK STATS =====
  const bookStatsLocal = safeObject(readLS(`bookStats_${userId}`, {}));
  const bookStatsServer = safeObject(u.bookStats);
  const bookStats = mergeBookStats(bookStatsLocal, bookStatsServer);

  // ===== STREAK =====
  const streakInfo = computeStreak(u, userId, daily);

  // ===== LESSONS =====
  const lessonRecords = safeObject(readLS(`lesson-records-${userId}`, {}));
  const lessonProgressBySubject = {};
  let lessonProgressCount = 0;
  const progressPrefix = `lesson-progress-${userId}-`;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith(progressPrefix)) continue;
      const p = safeJSON(localStorage.getItem(k), {});
      const ids = Array.isArray(p.completedStepIds)
        ? p.completedStepIds
        : Array.isArray(p.completedSteps)
        ? p.completedSteps
        : [];
      const n = ids.length;
      lessonProgressCount += n;
      const rest = k.slice(progressPrefix.length);
      const parts = rest.split('-');
      const subj = parts[1];
      if (subj) lessonProgressBySubject[subj] = (lessonProgressBySubject[subj] || 0) + n;
    }
  } catch {}

  // ===== EXAMS =====
  const allExams = safeExams.filter((e) => e && typeof e === 'object');

  const examResults = {};
  allExams.forEach((e) => {
    if (!e?.id) return;
    const r = safeArray(e.results).find((x) => idsEqual(x?.studentId, userId));
    examResults[e.id] = r?.isFinalSubmit === true;
  });

  const examsCompleted = allExams.filter((e) => examResults[e.id] === true);

  const examsActive = allExams.filter((e) => {
    if (!e) return false;
    const r = safeArray(e.results).find((x) => idsEqual(x?.studentId, userId));
    if (r?.isFinalSubmit) return false;
    if (e.statusType === 'archived' || e.statusType === 'pending') return false;
    const eg = normalizeGrade(e.grade);
    if (grade && eg && eg !== grade) return false;
    if (curriculumNeedsField(grade)) {
      const ef = normalizeField(e.field);
      if (ef && field && ef !== field) return false;
    }
    return true;
  });

  const examsChallenges = allExams.filter((e) => e?.isChallenge === true);
  const completedChallenges = examsChallenges.filter(
    (e) => examResults[e.id] === true
  ).length;

  const examsCorrections = allExams.filter((e) => {
    if (!e || e.statusType !== 'correction') return false;
    const r = safeArray(e.results).find((x) => idsEqual(x?.studentId, userId));
    return r && r.isEssayGraded === false;
  });

  const examsDrafts = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k?.startsWith('exam_')) continue;
      if (k.endsWith('_time') || k.endsWith('_step')) continue;
      const m = k.match(/^exam_(.+)_answers$/);
      if (!m) continue;
      const examId = m[1];
      const answers = safeJSON(localStorage.getItem(k), {});
      if (Object.keys(answers).length === 0) continue;
      examsDrafts[examId] = {
        answers,
        step: safeNumber(localStorage.getItem(`exam_${examId}_step`)),
        timeLeft: safeNumber(localStorage.getItem(`exam_${examId}_time`)),
      };
    }
  } catch {}

  // ===== ACHIEVEMENTS =====
  const xp = Number(u.xp) || 0;
  const achievementInfo = computeAchievements(u, userId, xp);

  // ===== PLANS =====
  const plans = safeArray(readLS(`curriculum_plans_${userId}`, []));
  const latestPlan = extractLatestPlan(plans);
  let todayPlan = null;
  if (latestPlan) {
    const age = latestPlan.createdAt ? daysAgo(latestPlan.createdAt) : 999;
    if (age < 14) {
      const todayIdx = persianDayIndex();
      const today = latestPlan.days?.[todayIdx];
      const sessions = safeArray(today?.sessions);
      if (sessions.length > 0) {
        todayPlan = {
          total: sessions.length,
          done: sessions.filter((s) => s?.done).length,
          sessions,
          planId: latestPlan.id,
          planTitle: latestPlan.title,
        };
      }
    }
  }

  // ===== TEAM =====
  const teamMembers = safeArray(readLS('teamMembers', []));
  const activeTeamMembers = teamMembers.filter((m) => m?.status === 'active').length;
  const pendingTeamMembers = teamMembers.filter((m) => m?.status === 'pending').length;
  const isInTeam = teamMembers.some((m) => idsEqual(m?.studentId, userId) && m?.status === 'active');

  // ===== MOOD =====
  const moodMap = safeObject(readLS(`mood_${userId}`, {}));
  const moodFromServer = safeObject(u.mood);
  const todayKey = localTodayStr();
  const moodToday = moodMap[todayKey] || moodFromServer[todayKey] || null;

  // ===== VIDEO =====
  const watchedVideos = safeObject(readLS('watchedVideos', {}));
  const watchedVideosCount = Object.keys(watchedVideos).length;

  // ===== COACH =====
  const dismissed = safeObject(readLS(`aiCoach_dismissed_${userId}_${new Date().toDateString()}`, {}));
  const progress = safeObject(readLS(`aiCoachProgress_${userId}_${todayKey}`, {}));
  const feedback = safeObject(readLS(`aiCoach_feedback_${userId}`, { events: [], successRates: {}, recentTypes: {} }));

  // ===== BOOKS =====
  const books = [];
  if (grade) {
    const prefix = `${grade}-`;
    const needsF = curriculumIsHighSchool(grade);
    const userField = field;
    Object.entries(COURSE_DETAILS || {}).forEach(([key, val]) => {
      if (!key.startsWith(prefix)) return;
      const parts = key.split('-');
      if (!needsF && parts.length !== 2) return;
      const subject = parts[parts.length - 1];
      const fieldPart = needsF && parts.length >= 3 ? parts[parts.length - 2] : null;
      if (needsF && userField && fieldPart && fieldPart !== userField) return;
      books.push({
        subject,
        field: fieldPart,
        title: val?.title || key,
        totalLessons: safeNumber(val?.lessons),
      });
    });
  }

  return {
    identity: {
      userId,
      role,
      grade,
      gradeRaw: u.grade || u.class || null,
      field,
      name: u.name || u.fullName || u.firstName || 'دوست من',
      firstName: String(u.firstName || (u.name || '').split(' ')[0] || 'دوست من').trim(),
      hasName: !!(u.name || u.firstName || u.fullName),
      createdAt: u.createdAt || null,
      accountAge: u.createdAt ? daysAgo(u.createdAt) : null,
    },
    time: {
      now: Date.now(),
      hour: new Date().getHours(),
      dayOfWeek: new Date().getDay(),
      persianDayIndex: persianDayIndex(),
      dayName: dayName(),
      isWeekend: [5, 6].includes(persianDayIndex()),
      isFriday: persianDayIndex() === 6,
      today: todayKey,
    },
    study: {
      daily,
      bookStats,
      streak: streakInfo.streak,
      streakLastDate: streakInfo.streakLastDate,
      lastActiveDays: streakInfo.lastActiveDays,
    },
    lessons: {
      records: lessonRecords,
      progress: lessonProgressBySubject,
      lessonProgressBySubject,
      lessonProgressCount,
      lessonRecordsCount: Object.keys(lessonRecords).length,
    },
    exams: {
      all: allExams,
      active: examsActive,
      completed: examsCompleted,
      challenges: examsChallenges,
      completedChallenges,
      drafts: examsDrafts,
      corrections: examsCorrections,
      examResults,
    },
    achievements: {
      unlockedCount: achievementInfo.unlockedCount,
      almostCount: achievementInfo.almostCount,
      almostDragons: achievementInfo.almostDragons,
    },
    planner: {
      plans,
      latestPlan,
      todayPlan,
      plansCount: plans.length,
    },
    team: {
      members: teamMembers,
      activeTeamMembers,
      pendingTeamMembers,
      isInTeam,
    },
    mood: {
      today: moodToday,
      hasMoodToday: !!moodToday,
    },
    video: {
      watched: watchedVideos,
      count: watchedVideosCount,
    },
    coach: {
      dismissed,
      progress,
      feedback,
      hasAnyFeedback: safeArray(feedback.events).length > 0,
      successRates: safeObject(feedback.successRates),
      recentTypes: safeObject(feedback.recentTypes),
    },
    economy: {
      xp,
      coins: Number(u.coins) || 0,
    },
    books,
  };
};

// ============================================================
// SECTION 4 — SIGNAL EXTRACTION
// ============================================================

const extractSignals = (data) => {
  if (!data) return null;
  const { identity, time, study, lessons, exams, achievements, planner, team, mood, video, coach, economy, books } = data;

  const signals = { identity, time };

  // Onboarding
  const onboardingScore = computeOnboardingScore(data);
  signals.onboarding = onboardingScore;

  // Temporal
  const nearestExam = exams.active
    .filter((e) => e.startTime || e.date)
    .map((e) => ({ exam: e, date: parseAnyDate(e.startTime || e.date) }))
    .filter((x) => x.date && x.date > new Date())
    .sort((a, b) => a.date - b.date)[0];

  signals.temporal = {
    hour: time.hour,
    isLateNight: time.hour >= 23 || time.hour < 5,
    isEarlyMorning: time.hour >= 5 && time.hour < 7,
    isSchoolHours: time.hour >= 8 && time.hour < 14,
    isEvening: time.hour >= 18 && time.hour < 23,
    isWeekend: time.isWeekend,
    isFriday: time.isFriday,
    hasPlanToday: !!planner.todayPlan,
    todayPlan: planner.todayPlan,
    daysToNearestExam: nearestExam
      ? Math.ceil((nearestExam.date.getTime() - Date.now()) / 86400000)
      : null,
    nearestExam,
    hasUnfinishedExam: Object.keys(exams.drafts).length > 0,
    draftCount: Object.keys(exams.drafts).length,
    hasPendingCorrection: exams.corrections.length > 0,
  };

  // Study
  const dailyArr = [];
  for (let i = 0; i < 90; i++) {
    dailyArr.push(safeNumber(study.daily[localDateKey(i)]));
  }
  const days7 = dailyArr.slice(0, 7);
  const days7prev = dailyArr.slice(7, 14);
  const days14 = dailyArr.slice(0, 14);
  const days30 = dailyArr.slice(0, 30);
  const days90 = dailyArr.slice(0, 90);

  const activeDays7 = days7.filter((x) => x > 0).length;
  const activeDays30 = days30.filter((x) => x > 0).length;
  const activeDays90 = days90.filter((x) => x > 0).length;

  signals.study = {
    total7: sum(days7),
    total7prev: sum(days7prev),
    total14: sum(days14),
    total30: sum(days30),
    total90: sum(days90),
    avg7: avg(days7),
    avg30: avg(days30),
    activeDays7,
    activeDays30,
    activeDays90,
    consistency7: stddev(days7.filter((x) => x > 0)),
    consistencyIndex: activeDays7 >= 3 ? 1 - stddev(days7) / (avg(days7) || 1) : 0,
    trend7vs7prev: sum(days7prev) === 0 ? (sum(days7) > 0 ? 2 : 0) : sum(days7) / sum(days7prev),
    trend7vs30: sum(days7) / (avg(days30) * 7 || 1),
    todayMinutes: days7[0],
    yesterdayMinutes: days7[1],
    longestStreak: study.streak,
    currentStreak: study.streak,
    lastActiveDays: study.lastActiveDays,
    lastActiveDate: study.streakLastDate,
    bookStats: study.bookStats,
    subjectDistribution: Object.fromEntries(
      Object.entries(study.bookStats).map(([s, m]) => [s, safeNumber(m)])
    ),
  };

  // Subjects
  signals.subjects = {};
  books.forEach((book) => {
    const { subject, title, totalLessons } = book;
    const matching = Object.values(lessons.records).filter(
      (rec) => rec && String(rec.grade) === identity.grade && rec.subject === subject
    );

    const lessonBest = {};
    matching.forEach((rec) => {
      const { folderName, lessonId, totalSteps } = rec;
      if (!lessonId || !totalSteps) return;
      const pk = `lesson-progress-${identity.userId}-${rec.grade}-${rec.subject}-${rec.folderName}-${rec.lessonId}`;
      const r = Math.min(1, (lessons.progress[pk] || 0) / totalSteps);
      if (!lessonBest[lessonId] || lessonBest[lessonId] < r) lessonBest[lessonId] = r;
    });

    const progress = totalLessons > 0
      ? Math.round((Object.values(lessonBest).reduce((a, c) => a + c, 0) / totalLessons) * 100)
      : 0;

    let lastVisit = 0;
    matching.forEach((r) => {
      const t = safeNumber(r?.lastVisited);
      if (t > lastVisit) lastVisit = t;
    });
    const recencyDays = lastVisit ? daysAgo(lastVisit) : 999;

    const examScores = [];
    const challengeScores = [];
    exams.all.forEach((e) => {
      if (!e || e.subject !== subject) return;
      const r = safeArray(e.results).find((x) => idsEqual(x?.studentId, identity.userId) && x?.isFinalSubmit);
      if (!r) return;
      const max = safeArray(e.questions).reduce((s, q) => s + safeNumber(q?.score, 1), 0) || 1;
      const pct = clamp((safeNumber(r.score) / max) * 100, 0, 100);
      (e.isChallenge ? challengeScores : examScores).push(pct);
    });

    const examAvg = examScores.length ? Math.round(avg(examScores)) : null;
    const challengeAvg = challengeScores.length ? Math.round(avg(challengeScores)) : null;
    const allScores = [examAvg, challengeAvg].filter((x) => x !== null);
    const scoreAvg = allScores.length ? Math.round(avg(allScores)) : null;

    let scoreTrend = 0;
    if (examScores.length >= 3) {
      const recent = avg(examScores.slice(-2));
      const older = avg(examScores.slice(0, -2));
      scoreTrend = older > 0 ? ((recent - older) / older) * 100 : 0;
    }

    let category = 'neutral';
    if (progress === 0 && recencyDays > 30) category = 'untouched';
    else if (recencyDays > 14) category = 'neglected';
    else if (progress < 40 || (scoreAvg !== null && scoreAvg < 50)) category = 'weakness';
    else if (progress > 60 && scoreAvg !== null && scoreAvg > 70 && recencyDays < 5) category = 'strength';
    else if (progress > 30 && progress < 60) category = 'building';

    let strategy = 'continue';
    if (category === 'untouched') strategy = 'start';
    else if (recencyDays > 14 && progress < 30) strategy = 'restart';
    else if (recencyDays > 7) strategy = 'revisit';
    else if (scoreAvg !== null && scoreAvg < 50) strategy = 'remediate';
    else if (scoreTrend < -15) strategy = 'stabilize';
    else if (scoreAvg !== null && scoreAvg > 80 && progress > 70) strategy = 'advance';
    else if (progress > 80 && recencyDays > 3) strategy = 'review';

    signals.subjects[subject] = {
      subject, title, progress, recencyDays,
      examAvg, challengeAvg, scoreAvg, scoreTrend,
      examCount: examScores.length,
      challengeCount: challengeScores.length,
      category, strategy,
      isMain: MAIN_SUBJECTS.includes(subject),
      totalLessons,
      minutes30d: safeNumber(study.bookStats[subject]),
      lastVisit,
    };
  });

  // Exams signals
  const allResults = exams.all.flatMap((e) => {
    if (!e) return [];
    const r = safeArray(e.results).find((x) => idsEqual(x?.studentId, identity.userId));
    if (!r || !r.isFinalSubmit) return [];
    return [{ exam: e, result: r }];
  });

  const scores = allResults.map(({ exam, result }) => {
    const max = safeArray(exam.questions).reduce((s, q) => s + safeNumber(q?.score, 1), 0) || 1;
    return {
      pct: (safeNumber(result.score) / max) * 100,
      date: parseAnyDate(result.submittedAt || exam.date)?.getTime() || 0,
    };
  }).filter((s) => s.date > 0).sort((a, b) => a.date - b.date);

  const recent5 = scores.slice(-5).map((s) => s.pct);
  const older5 = scores.slice(-10, -5).map((s) => s.pct);

  signals.exams = {
    totalTaken: scores.length,
    avgScore: scores.length ? Math.round(avg(scores.map((s) => s.pct))) : 0,
    passRate: scores.length ? scores.filter((s) => s.pct >= 50).length / scores.length : 0,
    recentAvg: recent5.length ? Math.round(avg(recent5)) : 0,
    olderAvg: older5.length ? Math.round(avg(older5)) : 0,
    recentTrend: older5.length ? Math.round(avg(recent5) - avg(older5)) : 0,
    activeCount: exams.active.length,
    challengesAvailable: exams.challenges.filter((e) => !exams.examResults[e.id]).length,
    publicExamsAvailable: exams.active.filter((e) => e.isGlobal && !e.isChallenge).length,
    teacherExamsAvailable: exams.active.filter((e) => !e.isGlobal && !e.isChallenge).length,
    pendingCorrections: exams.corrections.length,
    draftCount: Object.keys(exams.drafts).length,
    completedCount: exams.completed.length,
    completedChallenges: exams.completedChallenges,
    hasJustCompleted: scores.length > 0 && (Date.now() - scores[scores.length - 1].date) < 24 * 3600000,
    lastExamDate: scores.length ? scores[scores.length - 1].date : null,
    all: exams.all,
    active: exams.active,
    activeChallenges: exams.challenges,
    activePublicExams: exams.active.filter((e) => e.isGlobal && !e.isChallenge),
    activeTeacherExams: exams.active.filter((e) => !e.isGlobal && !e.isChallenge),
    corrections: exams.corrections,
    completed: exams.completed,
    drafts: exams.drafts,
    examResults: exams.examResults,
  };

  // Motivation
  signals.motivation = {
    coins: economy.coins,
    xp: economy.xp,
    medalsEarned: achievements.unlockedCount,
    almostMedals: achievements.almostDragons,
    almostMedalsCount: achievements.almostCount,
    completedChallenges: exams.completedChallenges,
    friendsCount: team.members.length,
    hasAvatar: !!readLS('selectedAvatar', null),
  };

  // Social
  const ranked = team.members
    .filter((m) => m?.status === 'active')
    .sort((a, b) => safeNumber(b?.score) - safeNumber(a?.score));
  const myRank = ranked.findIndex((f) => f?.studentId === identity.userId) + 1;

  signals.social = {
    teamSize: team.activeTeamMembers,
    isInTeam: team.isInTeam,
    pendingInTeam: team.pendingTeamMembers,
    myRankInTeam: myRank || 0,
    teamMembersCount: team.members.length,
  };

  // Planning
  const adherence = computePlanAdherence(planner.plans);
  signals.planning = {
    hasPlans: planner.plansCount > 0,
    plansCount: planner.plansCount,
    latestPlanCreatedAt: planner.latestPlan?.createdAt || null,
    latestPlanAge: planner.latestPlan?.createdAt ? daysAgo(planner.latestPlan.createdAt) : 999,
    adherence7d: adherence.rate7d,
    adherence30d: adherence.rate30d,
    todayPlan: planner.todayPlan,
    timetableFilled: false,
  };

  // Cognitive
  signals.cognitive = {
    preferredContentType: video.count > lessons.lessonRecordsCount ? 'video' : 'lesson',
    videoWatched: video.count,
    lessonsStarted: lessons.lessonRecordsCount,
  };

  // Daily (mood)
  signals.daily = {
    currentMood: mood.today,
    hasMoodToday: mood.hasMoodToday,
  };

  // Coach
  signals.coach = {
    dismissedToday: Object.keys(coach.dismissed).length,
    completedToday: Object.keys(coach.progress).length,
    hasAnyFeedback: coach.hasAnyFeedback,
    successRates: coach.successRates,
    recentTypes: coach.recentTypes,
  };

  // Meta
  const realDataScore =
    (signals.study.total7 > 0 ? 1 : 0) +
    (signals.exams.totalTaken > 0 ? 1 : 0) +
    (signals.cognitive.lessonsStarted > 0 ? 1 : 0) +
    (signals.cognitive.videoWatched > 0 ? 1 : 0) +
    (signals.planning.hasPlans ? 1 : 0) +
    (signals.motivation.medalsEarned > 0 ? 1 : 0) +
    (signals.social.isInTeam ? 1 : 0) +
    (signals.motivation.completedChallenges > 0 ? 1 : 0);

  signals.meta = {
    hasAnyData: signals.study.total30 > 0 || signals.exams.totalTaken > 0 || realDataScore > 0,
    dataAge: computeDataAge(study.daily),
    realDataScore,
    isCompleteBeginner: realDataScore <= 1,
    confidence: clamp(
      (signals.study.activeDays30 / 30) * 40 +
      (signals.exams.totalTaken > 5 ? 30 : signals.exams.totalTaken * 6) +
      (signals.motivation.medalsEarned > 2 ? 30 : signals.motivation.medalsEarned * 15),
      0, 100
    ) / 100,
  };

  return signals;
};

const computeOnboardingScore = (data) => {
  const checks = {
    hasGrade: !!data.identity.grade,
    hasName: data.identity.hasName,
    hasStudied: Object.keys(data.study.daily).length > 0,
    hasProgress: data.lessons.lessonProgressCount > 0,
    hasWatchedVideo: data.video.count > 0,
    hasTakenExam: data.exams.completed.length > 0,
    hasStartedChallenge: data.exams.completedChallenges > 0,
    hasCreatedPlan: data.planner.plansCount > 0,
    hasTeam: data.team.isInTeam,
  };
  const done = Object.values(checks).filter(Boolean).length;
  return {
    steps: checks,
    score: done,
    total: Object.keys(checks).length,
    percent: Math.round((done / Object.keys(checks).length) * 100),
    level:
      done === 0 ? 0 :
      done <= 2 ? 1 :
      done <= 4 ? 2 :
      done <= 6 ? 3 : 4,
  };
};

const computePlanAdherence = (plans) => {
  const arr = safeArray(plans);
  if (!arr.length) return { rate7d: 0, rate30d: 0 };
  const active = extractLatestPlan(arr);
  if (!active) return { rate7d: 0, rate30d: 0 };
  let total7 = 0, done7 = 0, total30 = 0, done30 = 0;
  const now = Date.now();
  safeArray(active.days).forEach((day) => {
    const d = parseAnyDate(day?.date);
    if (!d) return;
    const age = (now - d.getTime()) / 86400000;
    if (age > 30) return;
    const sessions = safeArray(day?.sessions);
    if (age <= 7) {
      total7 += sessions.length;
      done7 += sessions.filter((s) => s?.done).length;
    }
    total30 += sessions.length;
    done30 += sessions.filter((s) => s?.done).length;
  });
  return {
    rate7d: total7 > 0 ? done7 / total7 : 0,
    rate30d: total30 > 0 ? done30 / total30 : 0,
  };
};

const computeDataAge = (daily) => {
  const dates = Object.keys(safeObject(daily)).sort();
  if (!dates.length) return 0;
  return daysAgo(dates[0]);
};

// ============================================================
// SECTION 5 — PATTERN DETECTION
// ============================================================

const detectPatterns = (sig) => {
  const p = [];
  const push = (key, label, meta = {}) => p.push({ key, label, ...meta });

  if (sig.identity.role !== 'student') {
    // الگوهای نقش‌های غیر دانش‌آموز
    if (sig.identity.role === 'teacher') {
      push('teacher', '👨‍🏫 معلم');
      if (sig.social.teamSize === 0) push('teacher_no_team', '🏫 بدون تیم');
      if (sig.social.pendingInTeam > 0) push('teacher_pending', `⏳ ${toFa(sig.social.pendingInTeam)} درخواست`);
      if (sig.social.teamSize > 0) push('teacher_has_team', '✅ تیم فعال');
    } else if (sig.identity.role === 'parent') {
      push('parent', '👨‍👩‍👧 والد');
    } else if (sig.identity.role === 'admin') {
      push('admin', '⚙️ ادمین');
    }
    return p;
  }

  // ===== آنبوردینگ =====
  if (!sig.meta.hasAnyData) {
    push('brand_new', '🌟 کاملاً تازه‌وارد');
    if (!sig.identity.grade) push('no_grade_set', '📚 پایه هنوز تنظیم نشده');
    return p;
  }

  if (sig.meta.realDataScore <= 1) push('beginner', '🌱 تازه شروع کردی');
  if (sig.onboarding.score === 1) push('first_step_done', '✨ اولین قدم');
  if (sig.onboarding.score === 2) push('two_steps', '🚀 دو قدم اول');
  if (sig.onboarding.score >= 3 && sig.onboarding.score <= 5) push('onboarding_progress', '🎯 در حال کشف');

  // ===== بازگشت =====
  if (sig.study.lastActiveDays === 1 && sig.study.total7 > 0) push('yesterday_active', '👋 دیروز فعال بودی');
  if (sig.study.lastActiveDays >= 1 && sig.study.lastActiveDays <= 2 && sig.study.todayMinutes === 0) {
    push('recently_active_not_today', '⏰ امروز هنوز شروع نکردی');
  }
  if (sig.study.lastActiveDays >= 3 && sig.study.lastActiveDays <= 6) {
    push('welcome_back_3d', `📅 ${toFa(sig.study.lastActiveDays)} روز غیبت`);
  }
  if (sig.study.lastActiveDays >= 7 && sig.study.lastActiveDays <= 14) {
    push('welcome_back_7d', `⏳ ${toFa(sig.study.lastActiveDays)} روز دور بودی`);
  }
  if (sig.study.lastActiveDays >= 15 && sig.study.lastActiveDays <= 30) {
    push('welcome_back_14d', `🕰 ${toFa(sig.study.lastActiveDays)} روز غیبت`);
  }
  if (sig.study.lastActiveDays > 30) {
    push('welcome_back_long', `🕰 خیلی وقت بود نبودی`);
  }

  // ===== پایه =====
  if (sig.study.currentStreak >= 7) push('on_fire', `🔥 استریک ${toFa(sig.study.currentStreak)} روزه`);
  if (sig.study.currentStreak >= 30) push('legend_streak', `🏆 استریک ${toFa(sig.study.currentStreak)} روزه`);
  if (sig.study.currentStreak === 0 && sig.study.lastActiveDays <= 3 && sig.meta.dataAge > 7) {
    push('streak_broken', '💔 استریک شکسته');
  }

  // ===== روند =====
  if (sig.study.trend7vs7prev > 1.5) push('momentum_up', '📈 پر انرژی و صعودی');
  if (sig.study.trend7vs7prev < 0.5 && sig.study.total7prev > 60) push('slowing_down', '📉 کند شدن محسوس');
  if (sig.study.trend7vs7prev < 0.3 && sig.study.total7prev > 180) push('severe_slowdown', '🚨 کاهش شدید');
  if (sig.exams.recentTrend < -15) push('score_decline', '📉 افت نمرات');
  if (sig.exams.recentTrend > 15) push('score_improving', '📈 بهبود نمرات');

  // ===== نظم =====
  if (sig.study.activeDays7 >= 5) push('consistent', '✅ پیوستگی');
  if (sig.study.activeDays7 <= 1 && sig.meta.dataAge > 7) push('low_consistency', '⚠️ نظم پایین');
  if (sig.study.activeDays7 === 0 && sig.study.total30 > 60) push('temporary_pause', '⏸ وقفه موقت');
  if (sig.study.consistencyIndex > 0.6) push('rhythmic', '🎵 ریتم منظم');
  if (sig.study.consistencyIndex < 0.2 && sig.study.activeDays7 >= 4) push('irregular', '🎲 پراکندگی');

  // ===== موضوعی =====
  const weakSubjects = Object.values(sig.subjects).filter((s) => s.category === 'weakness');
  const strongSubjects = Object.values(sig.subjects).filter((s) => s.category === 'strength');
  const neglectedSubjects = Object.values(sig.subjects).filter((s) => s.category === 'neglected');
  const untouchedSubjects = Object.values(sig.subjects).filter((s) => s.category === 'untouched');
  const decliningSubjects = Object.values(sig.subjects).filter((s) => s.scoreTrend < -15);

  if (strongSubjects.length) push('has_strength', `💪 ${toFa(strongSubjects.length)} درس قوی`);
  if (weakSubjects.length) push('has_weakness', `🎯 ${toFa(weakSubjects.length)} درس نیازمند تقویت`);
  if (neglectedSubjects.length) push('has_neglected', `💤 ${toFa(neglectedSubjects.length)} درس رهاشده`);
  if (decliningSubjects.length) push('has_declining', `📉 افت در ${toFa(decliningSubjects.length)} درس`);
  if (untouchedSubjects.length >= 3 && sig.meta.realDataScore >= 2) {
    push('many_untouched', `🌾 ${toFa(untouchedSubjects.length)} درس دست‌نخورده`);
  }

  // تمرکز
  const distrib = Object.entries(sig.study.subjectDistribution).sort((a, b) => b[1] - a[1]);
  if (distrib.length >= 2 && distrib[0][1] > 0) {
    const total = sum(distrib.map(([, m]) => m)) || 1;
    if (distrib[0][1] / total > 0.6 && Object.keys(sig.subjects).length > 2) {
      push('subject_overfocus', '🔍 تمرکز افراطی');
    }
  }

  // ===== آزمون =====
  if (sig.exams.passRate < 0.4 && sig.exams.totalTaken > 5) push('chronic_failure', '🚨 شکست مزمن');
  if (sig.exams.passRate > 0.8 && sig.exams.totalTaken > 3) push('exam_master', '🏆 استاد آزمون');
  if (sig.exams.draftCount > 0) push('has_drafts', `📝 ${toFa(sig.exams.draftCount)} آزمون نیمه‌کاره`);
  if (sig.exams.pendingCorrections > 0) push('awaiting_correction', `⏳ ${toFa(sig.exams.pendingCorrections)} تصحیح`);
  if (sig.exams.hasJustCompleted) push('just_completed_exam', '✨ آزمون تازه');
  if (sig.exams.completedCount === 0 && sig.meta.realDataScore >= 1) push('never_took_exam', '📝 هنوز آزمونی ندادی');

  // ===== زمانی =====
  if (sig.temporal.isLateNight) push('late_night', '🌙 شب‌کاری');
  if (sig.temporal.isEarlyMorning) push('early_bird', '🌅 سحرخیز');
  if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 3) {
    push('exam_week', `📚 امتحان در ${toFa(sig.temporal.daysToNearestExam)} روز`);
  }
  if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 1) {
    push('exam_tomorrow', '🚨 امتحان فردا');
  }

  // ===== برنامه =====
  if (!sig.planning.hasPlans && sig.meta.dataAge > 3) push('no_plan', '📋 بدون برنامه');
  if (sig.planning.hasPlans && sig.planning.adherence7d < 0.4 && sig.meta.dataAge > 5) {
    push('plan_failure', '⚠️ عدم پایبندی');
  }
  if (sig.planning.hasPlans && sig.planning.adherence7d > 0.8) push('plan_master', '🎯 پایبندی عالی');
  if (sig.planning.latestPlanAge > 14) push('stale_plan', '📋 برنامه قدیمی');
  if (sig.planning.todayPlan && sig.planning.todayPlan.total > 0 && sig.planning.todayPlan.done === 0) {
    push('plan_missed_today', '⏰ برنامه امروز انجام نشد');
  }

  // ===== انگیزشی =====
  if (sig.motivation.almostMedalsCount > 0) push('almost_medal', `🏅 ${toFa(sig.motivation.almostMedalsCount)} مدال نزدیک`);
  if (sig.motivation.medalsEarned === 0 && sig.meta.realDataScore >= 2) push('no_medals_yet', '🎖 بدون مدال');
  if (sig.motivation.completedChallenges === 0 && sig.exams.challengesAvailable > 0) {
    push('never_challenged', '⚔️ هنوز چالشی نزدی');
  }

  // ===== اجتماعی =====
  if (!sig.social.isInTeam) push('no_team', '👥 عضو گروهی نیستی');
  if (sig.social.isInTeam && sig.social.teamSize > 0 && sig.social.myRankInTeam === 1) {
    push('leader', '👑 رتبه اول');
  }

  // ===== مراقبت =====
  if (sig.study.avg7 > 180 && sig.study.activeDays7 >= 6) push('overwork_risk', '🚨 خطر فرسودگی');
  if (sig.study.todayMinutes === 0 && sig.temporal.isEvening) push('no_study_today_late', '⏰ هنوز شروع نکردی');
  if (sig.study.todayMinutes >= 60) push('productive_today', `✅ ${toFa(sig.study.todayMinutes)} دقیقه`);

  // ===== محتوایی =====
  if (sig.cognitive.videoWatched > 3 && sig.cognitive.lessonsStarted < 2) {
    push('video_heavy', '🎬 تمایل به ویدیو');
  }
  if (sig.cognitive.videoWatched === 0 && sig.cognitive.lessonsStarted === 0 && sig.meta.realDataScore >= 1) {
    push('no_learning_content', '📖 هنوز محتوایی باز نکردی');
  }
  if (sig.cognitive.lessonsStarted > 0 && sig.study.bookStats && Object.keys(sig.study.bookStats).length === 0) {
    push('has_book_no_stats', '📚 کتاب باز کردی ولی مطالعه ثبت نشد');
  }

  // ===== بهبود =====
  if (sig.study.lastActiveDays > 30) push('long_absent', '🕰 غیبت طولانی');
  if (sig.study.todayMinutes > 0 && sig.study.todayMinutes < 10) push('short_study_today', `⏱ فقط ${toFa(sig.study.todayMinutes)} دقیقه`);

  return p;
};

// ============================================================
// SECTION 6 — STATE CLASSIFICATION
// ============================================================

const classifyState = (sig, patterns) => {
  if (sig.identity.role === 'teacher') return classifyTeacher(sig, patterns);
  if (sig.identity.role === 'parent') return classifyParent(sig, patterns);
  if (sig.identity.role === 'admin') return classifyAdmin(sig, patterns);
  return classifyStudent(sig, patterns);
};

const classifyStudent = (sig, patterns) => {
  const keys = new Set(safeArray(patterns).map((x) => x.key));
  const overall = classifyOverallStudent(sig, keys);
  const mode = STATE_TO_MODE[overall] || ADVISOR_MODES.COACH;

  return {
    overall,
    mode,
    subjects: Object.fromEntries(
      Object.entries(sig.subjects).map(([k, v]) => [
        k, { state: v.category, score: v.scoreAvg, strategy: v.strategy, trend: v.scoreTrend },
      ])
    ),
    motivation: classifyMotivation(sig),
    temporal: classifyTemporal(sig),
    urgency: computeUrgency(sig),
    onboardingLevel: sig.onboarding.level,
  };
};

const classifyOverallStudent = (sig, keys) => {
  if (!sig.meta.hasAnyData || sig.meta.realDataScore === 0) {
    if (!sig.identity.grade) return USER_STATES.NEW_VISITOR;
    if (sig.onboarding.score === 0) return USER_STATES.NEW_VISITOR;
  }

  if (sig.meta.realDataScore <= 3 && sig.meta.dataAge <= 7) {
    if (sig.onboarding.score <= 1) return USER_STATES.ONBOARDING_1;
    if (sig.onboarding.score === 2) return USER_STATES.ONBOARDING_2;
    if (sig.onboarding.score === 3) return USER_STATES.ONBOARDING_3;
  }

  if (sig.study.lastActiveDays > 14) return USER_STATES.LOST;
  if (sig.study.lastActiveDays >= 3 && sig.study.lastActiveDays <= 14) return USER_STATES.RETURNING;

  if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 5 && sig.exams.activeCount > 0) {
    return USER_STATES.EXAM_WEEK;
  }

  if (keys.has('overwork_risk') || keys.has('severe_slowdown')) return USER_STATES.BURNT_OUT;

  if (sig.exams.avgScore > 0 && sig.exams.avgScore < 50 && sig.exams.totalTaken > 2) return USER_STATES.STRUGGLING;
  if (sig.exams.avgScore > 85 && sig.study.activeDays7 >= 4) return USER_STATES.EXCELLING;
  if (sig.exams.avgScore >= 50 && sig.exams.avgScore < 70) return USER_STATES.BUILDING;

  if (sig.study.activeDays7 < 3 && sig.study.activeDays30 > 10) return USER_STATES.PLATEAU;

  return USER_STATES.STEADY;
};

const classifyTeacher = (sig, patterns) => {
  const keys = new Set(safeArray(patterns).map((x) => x.key));
  let overall = USER_STATES.TEACHER_ACTIVE;

  if (keys.has('teacher_no_team')) overall = USER_STATES.TEACHER_NEW;
  else if (keys.has('teacher_pending')) overall = USER_STATES.TEACHER_TEAM_BUILDER;
  else if (sig.social.teamSize > 0 && sig.exams.activeCount === 0) overall = USER_STATES.TEACHER_EXAM_MAKER;

  return {
    overall,
    mode: STATE_TO_MODE[overall] || ADVISOR_MODES.TEACHER_COACH,
    subjects: {},
    motivation: 'MEDIUM',
    temporal: classifyTemporal(sig),
    urgency: 0,
    onboardingLevel: 0,
  };
};

const classifyParent = (sig, patterns) => {
  let overall = USER_STATES.PARENT_ACTIVE;
  if (!sig.meta.hasAnyData) overall = USER_STATES.PARENT_NEW;
  return {
    overall,
    mode: STATE_TO_MODE[overall] || ADVISOR_MODES.PARENT_SUPPORTER,
    subjects: {},
    motivation: 'MEDIUM',
    temporal: classifyTemporal(sig),
    urgency: 0,
    onboardingLevel: 0,
  };
};

const classifyAdmin = (sig, patterns) => {
  let overall = USER_STATES.ADMIN_ACTIVE;
  if (!sig.meta.hasAnyData) overall = USER_STATES.ADMIN_NEW;
  return {
    overall,
    mode: STATE_TO_MODE[overall] || ADVISOR_MODES.ADMIN_COACH,
    subjects: {},
    motivation: 'MEDIUM',
    temporal: classifyTemporal(sig),
    urgency: 0,
    onboardingLevel: 0,
  };
};

const classifyMotivation = (sig) => {
  if (sig.motivation.almostMedalsCount > 0) return 'HIGH';
  if (sig.study.currentStreak >= 7) return 'HIGH';
  if (sig.motivation.completedChallenges === 0 && sig.motivation.medalsEarned === 0 && sig.meta.dataAge > 3) return 'LOW';
  if (sig.study.total7 === 0 && sig.meta.dataAge > 3) return 'LOW';
  return 'MEDIUM';
};

const classifyTemporal = (sig) => {
  if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 3) return 'CRITICAL';
  if (sig.temporal.hasPlanToday) return 'PLANNED';
  if (sig.temporal.isWeekend) return 'RELAXED';
  return 'NORMAL';
};

const computeUrgency = (sig) => {
  let u = 0;
  if (sig.temporal.daysToNearestExam !== null) {
    if (sig.temporal.daysToNearestExam <= 1) u += 40;
    else if (sig.temporal.daysToNearestExam <= 3) u += 25;
    else if (sig.temporal.daysToNearestExam <= 7) u += 10;
  }
  u += sig.exams.draftCount * 15;
  u += sig.exams.pendingCorrections * 5;
  return clamp(u, 0, 100);
};

// ============================================================
// SECTION 7 — CANDIDATE GENERATION
// ============================================================

const generateCandidates = (sig, state) => {
  if (sig.identity.role === 'teacher') return generateTeacherCandidates(sig, state);
  if (sig.identity.role === 'parent') return generateParentCandidates(sig, state);
  if (sig.identity.role === 'admin') return generateAdminCandidates(sig, state);
  return generateStudentCandidates(sig, state);
};

const generateStudentCandidates = (sig, state) => {
  const c = [];
  const push = (item) => c.push(item);

  // ===== آنبوردینگ =====
  if (state.overall === USER_STATES.NEW_VISITOR || state.overall === USER_STATES.ONBOARDING_1) {
    if (!sig.identity.grade) {
      push({
        type: SUGGESTION_TYPES.SET_GRADE,
        title: 'پایه تحصیلی‌ات را انتخاب کن',
        reason: 'تا برنامه‌ات دقیق بسازم',
        goal: 'شروع',
        weight: 5.0,
        urgency: 10,
      });
    } else {
      if (sig.cognitive.lessonsStarted === 0 && sig.study.bookStats && Object.keys(sig.study.bookStats).length === 0) {
        push({
          type: SUGGESTION_TYPES.PICK_FIRST_BOOK,
          title: 'یه کتاب انتخاب کن و شروع کن',
          reason: 'هر سفر با یه قدم شروع می‌شه',
          goal: 'شروع',
          weight: 4.5,
          urgency: 9,
        });
      }
      if (sig.cognitive.videoWatched === 0) {
        push({
          type: SUGGESTION_TYPES.WATCH_FIRST_VIDEO,
          title: 'اولین ویدیوی آموزشی‌ات را ببین',
          reason: 'راحت‌ترین راه شروع',
          goal: 'شروع',
          weight: 4.0,
          urgency: 7,
        });
      }
      if (sig.exams.totalTaken === 0 && sig.exams.activeCount > 0) {
        push({
          type: SUGGESTION_TYPES.TAKE_FIRST_QUIZ,
          title: 'یه آزمون کوچک امتحان کن',
          reason: 'سطحت را بسنج',
          goal: 'کشف',
          weight: 3.5,
          urgency: 6,
        });
      }
      push({
        type: SUGGESTION_TYPES.MEET_THE_APP,
        title: 'با اپ آشنا شو',
        reason: 'یه گشت کوتاه',
        goal: 'کشف',
        weight: 2.0,
        urgency: 3,
      });
    }
  }

  if (state.overall === USER_STATES.ONBOARDING_2 || state.overall === USER_STATES.ONBOARDING_3) {
    if (sig.cognitive.lessonsStarted === 0) {
      push({
        type: SUGGESTION_TYPES.COMPLETE_FIRST_LESSON,
        title: 'اولین درس را کامل کن',
        reason: 'قدم بزرگی به جلو',
        goal: 'پیشرفت',
        weight: 3.0,
        urgency: 6,
      });
    }
    if (sig.exams.totalTaken === 0 && sig.exams.activeCount > 0) {
      push({
        type: SUGGESTION_TYPES.TAKE_FIRST_QUIZ,
        title: 'اولین آزمون را بده',
        reason: 'اولین مدال را بگیر',
        goal: 'انگیزش',
        weight: 2.8,
        urgency: 5,
      });
    }
    if (!sig.planning.hasPlans) {
      push({
        type: SUGGESTION_TYPES.CREATE_PLAN,
        title: 'یه برنامه ساده بساز',
        reason: 'موفقیت با برنامه شروع می‌شه',
        goal: 'نظم',
        weight: 2.5,
        urgency: 4,
      });
    }
    if (sig.social.isInTeam) {
      push({
        type: SUGGESTION_TYPES.LEADERBOARD,
        title: 'رتبه‌ات را در تیم ببین',
        reason: 'با هم‌تیمی‌ها رقابت کن',
        goal: 'انگیزش',
        weight: 2.0,
      });
    }
    if (sig.cognitive.videoWatched > 0 && sig.cognitive.lessonsStarted === 0) {
      push({
        type: SUGGESTION_TYPES.COMPLETE_FIRST_LESSON,
        title: 'اولین درس را کامل کن',
        reason: 'از ویدیو به درس برو',
        goal: 'پیشرفت',
        weight: 2.5,
      });
    }
  }

  if (sig.meta.realDataScore >= 2) {
    // Draft
    if (sig.temporal.hasUnfinishedExam) {
      push({
        type: SUGGESTION_TYPES.RESUME_DRAFT,
        title: 'ادامه آزمون نیمه‌کاره',
        reason: `${toFa(sig.temporal.draftCount)} آزمون رهاشده`,
        goal: 'فوریت',
        weight: 3.0,
        urgency: 10,
      });
    }

    // Corrections
    if (sig.exams.pendingCorrections > 0) {
      push({
        type: SUGGESTION_TYPES.WAIT_CORRECTION,
        title: 'منتظر تصحیح معلم',
        reason: `${toFa(sig.exams.pendingCorrections)} آزمون در انتظار`,
        goal: 'اطلاع',
        weight: 0.7,
      });
    }

    // Subjects
    Object.values(sig.subjects).forEach((subj) => {
      if (subj.category === 'weakness') {
        push({
          type: SUGGESTION_TYPES.STUDY,
          subject: subj.subject,
          title: `تقویت ${subj.title}`,
          reason: subj.scoreAvg !== null
            ? `میانگین ${toFa(subj.scoreAvg)}٪`
            : `پیشرفت فقط ${toFa(subj.progress)}٪`,
          goal: 'ضعف',
          weight: 1.6,
          urgency: 6,
        });
        if (subj.progress > 15) {
          push({
            type: SUGGESTION_TYPES.REVIEW,
            subject: subj.subject,
            title: `مرور ${subj.title}`,
            duration: 15,
            reason: 'تثبیت یادگیری',
            goal: 'ضعف',
            weight: 1.1,
          });
        }
      }
      if (subj.category === 'neglected') {
        push({
          type: SUGGESTION_TYPES.STUDY,
          subject: subj.subject,
          title: `بازگشت به ${subj.title}`,
          reason: `${toFa(subj.recencyDays)} روزه رهاش کردی`,
          goal: 'احیاء',
          weight: 1.4,
          urgency: 5,
        });
      }
      if (subj.category === 'untouched' && subj.isMain) {
        push({
          type: SUGGESTION_TYPES.EXPLORE,
          subject: subj.subject,
          title: `شروع ${subj.title}`,
          reason: 'هنوز دست نزدی',
          goal: 'کشف',
          weight: 1.0,
        });
      }
      if (subj.strategy === 'advance') {
        push({
          type: SUGGESTION_TYPES.STUDY,
          subject: subj.subject,
          title: `پیشروی در ${subj.title}`,
          reason: `روند درخشان (${toFa(subj.scoreAvg)}٪)`,
          goal: 'قوت',
          weight: 1.2,
        });
      }
      if (subj.strategy === 'stabilize') {
        push({
          type: SUGGESTION_TYPES.REVIEW_MISTAKES,
          subject: subj.subject,
          title: `تحلیل افت ${subj.title}`,
          reason: `افت ${toFa(Math.abs(Math.round(subj.scoreTrend)))}٪`,
          goal: 'تثبیت',
          weight: 1.5,
          urgency: 7,
        });
      }
    });

    // Challenges
    safeArray(sig.exams.activeChallenges).slice(0, 3).forEach((exam) => {
      if (!exam?.id) return;
      push({
        type: SUGGESTION_TYPES.CHALLENGE,
        subject: exam.subject,
        title: exam.title || `چالش ${exam.subject}`,
        examId: exam.id,
        reason: 'چالش منتظر توست',
        goal: 'چالش',
        weight: 1.3,
        urgency: 5,
      });
    });

    // Public exams
    safeArray(sig.exams.activePublicExams).slice(0, 2).forEach((exam) => {
      if (!exam?.id) return;
      push({
        type: SUGGESTION_TYPES.EXAM,
        subject: exam.subject,
        title: exam.title || `آزمون ${exam.subject}`,
        examId: exam.id,
        reason: 'سنجش سطح یادگیری',
        goal: 'آزمون',
        weight: 1.0,
      });
    });

    // Teacher exams
    if (safeArray(sig.exams.activeTeacherExams).length > 0) {
      const exam = sig.exams.activeTeacherExams[0];
      if (exam?.id) {
        push({
          type: SUGGESTION_TYPES.EXAM,
          subject: exam.subject,
          title: exam.title,
          examId: exam.id,
          reason: 'آزمون کلاسی معلم',
          goal: 'آزمون',
          weight: 1.2,
          urgency: 4,
        });
      }
    }

    // Plan today
    if (sig.planning.todayPlan && sig.planning.todayPlan.done < sig.planning.todayPlan.total) {
      push({
        type: SUGGESTION_TYPES.FOLLOW_PLAN,
        title: 'ادامه برنامه‌ی امروز',
        reason: `${toFa(sig.planning.todayPlan.done)} از ${toFa(sig.planning.todayPlan.total)} انجام شد`,
        goal: 'برنامه',
        weight: 1.9,
        urgency: 7,
      });
    }

    // Almost medal
    if (sig.motivation.almostMedalsCount > 0) {
      push({
        type: SUGGESTION_TYPES.ALMOST_MEDAL,
        title: `${toFa(sig.motivation.almostMedalsCount)} مدال نزدیک باز شدن`,
        reason: 'فقط چند قدم مانده',
        goal: 'انگیزش',
        weight: 1.5,
        urgency: 3,
      });
    }

    // Streak
    if (sig.study.currentStreak >= 3 && sig.study.todayMinutes === 0) {
      push({
        type: SUGGESTION_TYPES.STREAK_CHECK,
        title: `استریک ${toFa(sig.study.currentStreak)} روزه‌ات را حفظ کن`,
        reason: 'امروز هنوز شروع نکردی',
        goal: 'حفظ استریک',
        weight: 1.7,
        urgency: 8,
      });
    }

    // Celebrate
    if (sig.exams.hasJustCompleted && sig.exams.recentAvg >= 80) {
      push({
        type: SUGGESTION_TYPES.CELEBRATE,
        title: 'جشن بگیر!',
        reason: 'عملکرد فوق‌العاده‌ای داشتی',
        goal: 'انگیزش',
        weight: 1.0,
      });
    }

    // Medals
    if (sig.motivation.medalsEarned > 0) {
      push({
        type: SUGGESTION_TYPES.VIEW_MEDALS,
        title: 'گالری افتخاراتت',
        reason: `${toFa(sig.motivation.medalsEarned)} مدال داری`,
        goal: 'انگیزش',
        weight: 0.7,
      });
    }

    // Leaderboard
    if (sig.social.teamSize > 2) {
      push({
        type: SUGGESTION_TYPES.LEADERBOARD,
        title: 'جدول رتبه‌بندی',
        reason: sig.social.myRankInTeam <= 3 ? 'رتبه‌ات را ببین' : 'برای بهتر شدن انگیزه بگیر',
        goal: 'رقابت',
        weight: 0.8,
      });
    }

    // Join team
    if (!sig.social.isInTeam) {
      push({
        type: SUGGESTION_TYPES.JOIN_GROUP,
        title: 'به یک گروه بپیوند',
        reason: 'یادگیری گروهی مؤثرتر است',
        goal: 'اجتماعی',
        weight: 0.8,
      });
    }

    // Break
    if (sig.study.avg7 > 180 && sig.study.activeDays7 >= 6) {
      push({
        type: SUGGESTION_TYPES.TAKE_BREAK,
        title: 'یه استراحت واقعی',
        reason: 'این هفته زیاد فشار آوردی',
        goal: 'سلامت',
        weight: 1.6,
        urgency: 4,
      });
    }

    // Sleep
    if (sig.temporal.isLateNight) {
      push({
        type: SUGGESTION_TYPES.SLEEP_REMINDER,
        title: 'وقت خوابه',
        reason: 'خواب کافی برای مغز ضروری است',
        goal: 'سلامت',
        weight: 2.2,
        urgency: 9,
      });
    }

    // Mood
    if (!sig.daily.hasMoodToday && sig.temporal.hour >= 8 && sig.temporal.hour <= 22 && sig.meta.dataAge > 3) {
      push({
        type: SUGGESTION_TYPES.MOOD_CHECK,
        title: 'امروز چه حسی داری؟',
        reason: 'حالت روحی روی یادگیری اثر دارد',
        goal: 'خودآگاهی',
        weight: 0.6,
      });
    }

    // Explore strength
    const strengthSubject = Object.values(sig.subjects).find((s) => s.category === 'strength');
    if (strengthSubject) {
      push({
        type: SUGGESTION_TYPES.ADVANCE_TOPIC,
        subject: strengthSubject.subject,
        title: `مبحث جدید ${strengthSubject.title}`,
        reason: 'در این درس قوی هستی',
        goal: 'رشد',
        weight: 1.0,
      });
    }

    // Continue learning
    const recent = Object.values(sig.subjects).sort((a, b) => b.lastVisit - a.lastVisit)[0];
    if (recent && recent.progress > 0 && recent.progress < 100 && recent.recencyDays < 7) {
      push({
        type: SUGGESTION_TYPES.EXPLORE,
        subject: recent.subject,
        title: `ادامه‌ی ${recent.title}`,
        reason: 'آخرین بار اینجا بودی',
        goal: 'ادامه',
        weight: 1.2,
      });
    }

    // Finish lesson
    Object.values(sig.subjects).forEach((subj) => {
      if (subj.progress > 20 && subj.progress < 90 && subj.recencyDays < 14) {
        push({
          type: SUGGESTION_TYPES.FINISH_LESSON,
          subject: subj.subject,
          title: `تمام کردن ${subj.title}`,
          reason: `${toFa(subj.progress)}٪ پیشرفت`,
          goal: 'تکمیل',
          weight: 1.1,
        });
      }
    });

    // Exam week
    if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 7) {
      const examSubject = sig.temporal.nearestExam?.exam?.subject;
      if (examSubject && sig.subjects[examSubject]) {
        push({
          type: SUGGESTION_TYPES.READ_TEXTBOOK,
          subject: examSubject,
          title: `کتاب درسی ${sig.subjects[examSubject].title}`,
          duration: 45,
          reason: 'تمرکز قبل از امتحان',
          goal: 'امتحان',
          weight: 1.8,
          urgency: 8,
        });
      }
    }

    // Exercise
    const buildingSubj = Object.values(sig.subjects).find((s) => s.category === 'building');
    if (buildingSubj) {
      push({
        type: SUGGESTION_TYPES.DO_EXERCISE,
        subject: buildingSubj.subject,
        title: `تمرین عملی ${buildingSubj.title}`,
        reason: 'تثبیت با تمرین',
        goal: 'تمرین',
        weight: 1.0,
      });
    }

    // Learn skill
    if (sig.study.total30 > 300 && sig.exams.avgScore < 60 && sig.exams.totalTaken >= 3) {
      push({
        type: SUGGESTION_TYPES.LEARN_SKILL,
        title: 'روش‌های مطالعه‌ی مؤثر',
        reason: 'زیاد می‌خوانی ولی نتیجه کم است',
        goal: 'مهارت',
        weight: 1.4,
        urgency: 6,
      });
    }

    // Set goal
    if (sig.planning.adherence7d < 0.3 && sig.meta.dataAge > 5) {
      push({
        type: SUGGESTION_TYPES.SET_GOAL,
        title: 'هدف هفتگی برای خودت بذار',
        reason: 'هدف، انگیزه می‌سازد',
        goal: 'هدف',
        weight: 0.9,
      });
    }

    // Parent view
    if (sig.exams.totalTaken >= 3) {
      push({
        type: SUGGESTION_TYPES.PARENT_VIEW,
        title: 'گزارش والدین',
        reason: 'روند پیشرفتت را ببین',
        goal: 'آگاهی',
        weight: 0.5,
      });
    }

    // Mentor
    if (sig.social.isInTeam && sig.social.teamSize > 2) {
      push({
        type: SUGGESTION_TYPES.MENTOR_FRIEND,
        title: 'به هم‌تیمی‌هات کمک کن',
        reason: 'یادگیری با آموزش عمیق‌تر می‌شه',
        goal: 'اجتماعی',
        weight: 0.7,
      });
    }
  }

  // ===== Welcome back =====
  if (state.overall === USER_STATES.RETURNING || state.overall === USER_STATES.LOST) {
    push({
      type: SUGGESTION_TYPES.WELCOME_BACK,
      title: 'خوش برگشتی!',
      reason: sig.study.lastActiveDays > 0 ? `${toFa(sig.study.lastActiveDays)} روز دور بودی` : 'دلمون برات تنگ شده بود',
      goal: 'بازگشت',
      weight: 2.5,
      urgency: 7,
    });
    push({
      type: SUGGESTION_TYPES.RESTART_JOURNEY,
      title: 'سفر جدید شروع کن',
      reason: 'از نو شروع کنیم',
      goal: 'بازگشت',
      weight: 2.0,
      urgency: 5,
    });
    if (sig.study.yesterdayMinutes > 0 || sig.study.total7prev > 0) {
      push({
        type: SUGGESTION_TYPES.RESUME_YESTERDAY,
        title: 'ادامه‌ی روزهای خوبت',
        reason: 'قبلاً فعال بودی',
        goal: 'حفظ روند',
        weight: 1.5,
      });
    }
  }

  // ===== Fix streak =====
  if (sig.study.currentStreak === 0 && sig.study.lastActiveDays <= 3 && sig.meta.dataAge > 7) {
    push({
      type: SUGGESTION_TYPES.FIX_STREAK,
      title: 'استریک جدید شروع کن',
      reason: 'امروز بهترین روزه',
      goal: 'انگیزش',
      weight: 1.5,
    });
  }

  // ===== Resume yesterday =====
  if (sig.study.yesterdayMinutes > 0 && sig.study.todayMinutes === 0 && sig.temporal.hour >= 10) {
    push({
      type: SUGGESTION_TYPES.RESUME_YESTERDAY,
      title: 'دیروز فعال بودی، امروز هم شروع کن',
      reason: `دیروز ${toFa(sig.study.yesterdayMinutes)} دقیقه`,
      goal: 'حفظ روند',
      weight: 1.3,
    });
  }

  return c;
};

const generateTeacherCandidates = (sig, state) => {
  const c = [];
  const push = (item) => c.push(item);

  if (sig.social.teamSize === 0) {
    push({
      type: SUGGESTION_TYPES.CREATE_TEAM,
      title: 'اولین تیمت را بساز',
      reason: 'شاگردانت منتظرند',
      goal: 'شروع',
      weight: 5.0,
      urgency: 10,
    });
  } else {
    if (sig.social.pendingInTeam > 0) {
      push({
        type: SUGGESTION_TYPES.RESPOND_REQUESTS,
        title: 'به درخواست‌های تیم رسیدگی کن',
        reason: `${toFa(sig.social.pendingInTeam)} درخواست منتظر`,
        goal: 'مدیریت',
        weight: 4.5,
        urgency: 9,
      });
    }
    if (sig.exams.activeCount === 0) {
      push({
        type: SUGGESTION_TYPES.CREATE_EXAM,
        title: 'اولین آزمون کلاس را بساز',
        reason: 'سطح شاگردانت را بسنج',
        goal: 'تدریس',
        weight: 3.5,
        urgency: 6,
      });
    }
    push({
      type: SUGGESTION_TYPES.VIEW_TEAM_ACTIVITY,
      title: 'فعالیت تیمت را ببین',
      reason: `${toFa(sig.social.teamSize)} عضو فعال`,
      goal: 'مدیریت',
      weight: 2.5,
      urgency: 3,
    });
    push({
      type: SUGGESTION_TYPES.PUBLISH_PROJECT,
      title: 'محتوای جدید منتشر کن',
      reason: 'به تیمت محتوای تازه بده',
      goal: 'تولید',
      weight: 2.0,
    });
  }
  return c;
};

const generateParentCandidates = (sig, state) => {
  const c = [];
  const push = (item) => c.push(item);

  if (state.overall === USER_STATES.PARENT_NEW || !sig.meta.hasAnyData) {
    push({
      type: SUGGESTION_TYPES.SET_CHILD_PROFILE,
      title: 'پروفایل فرزندت را کامل کن',
      reason: 'برای شروع رهگیری',
      goal: 'شروع',
      weight: 5.0,
      urgency: 10,
    });
  } else {
    push({
      type: SUGGESTION_TYPES.VIEW_CHILD_PROGRESS,
      title: 'پیشرفت فرزندت را ببین',
      reason: 'گزارش کامل',
      goal: 'آگاهی',
      weight: 4.0,
      urgency: 5,
    });
    if (sig.exams.totalTaken > 0) {
      push({
        type: SUGGESTION_TYPES.VIEW_CHILD_EXAMS,
        title: 'آزمون‌های فرزندت را ببین',
        reason: `${toFa(sig.exams.totalTaken)} آزمون داده`,
        goal: 'آگاهی',
        weight: 3.0,
      });
    }
    if (sig.social.isInTeam) {
      push({
        type: SUGGESTION_TYPES.VIEW_CHILD_TEAM,
        title: 'تیم فرزندت را ببین',
        reason: 'فعالیت اجتماعی',
        goal: 'آگاهی',
        weight: 2.5,
      });
    }
  }
  return c;
};

const generateAdminCandidates = (sig, state) => {
  const c = [];
  const push = (item) => c.push(item);

  push({
    type: SUGGESTION_TYPES.REVIEW_PLATFORM,
    title: 'آمار پلتفرم را ببین',
    reason: 'وضعیت کلی',
    goal: 'مدیریت',
    weight: 3.0,
    urgency: 4,
  });
  push({
    type: SUGGESTION_TYPES.REVIEW_USERS,
    title: 'کاربران را بررسی کن',
    reason: 'مدیریت کاربران',
    goal: 'مدیریت',
    weight: 2.5,
  });
  return c;
};

// ============================================================
// SECTION 8 — SCORING
// ============================================================

const MODE_TYPE_BONUS = {
  GUIDE: { set_grade: 0.8, pick_first_book: 0.8, watch_first_video: 0.7, complete_first_lesson: 0.7, take_first_quiz: 0.6, meet_the_app: 0.6, claim_welcome: 0.5 },
  NURTURER: { study: 0.4, explore: 0.4, pick_first_book: 0.3, complete_first_lesson: 0.3, take_first_quiz: 0.3, streak_check: 0.2, mood_check: 0.3 },
  RECOVERER: { study: 0.35, review: 0.3, streak_check: 0.4, fix_streak: 0.4, welcome_back: 0.5, restart_journey: 0.4 },
  PROTECTOR: { take_break: 0.5, sleep_reminder: 0.5, mood_check: 0.3, review: 0.2 },
  SUPPORTER: { study: 0.35, review: 0.3, review_mistakes: 0.4, learn_skill: 0.3 },
  COACH: { study: 0.25, follow_plan: 0.3, read_textbook: 0.25, exam: 0.15 },
  PARTNER: { challenge: 0.25, exam: 0.25, follow_plan: 0.25, study: 0.15 },
  CHALLENGER: { challenge: 0.45, exam: 0.35, view_medals: 0.15, almost_medal: 0.3, advance_topic: 0.3 },
  STRATEGIST: { read_textbook: 0.4, review_mistakes: 0.4, exam: 0.3, follow_plan: 0.3 },
  CRISIS_MANAGER: { resume_draft: 0.6, follow_plan: 0.4, review_mistakes: 0.5, exam: 0.3 },
  CHEERLEADER: { celebrate: 0.5, almost_medal: 0.4, view_medals: 0.3, streak_check: 0.3 },
  TEACHER_COACH: { create_team: 0.5, create_exam: 0.4, view_team_activity: 0.3, publish_project: 0.3, respond_requests: 0.4 },
  TEACHER_GUIDE: { create_team: 0.6 },
  PARENT_GUIDE: { set_child_profile: 0.6 },
  PARENT_SUPPORTER: { view_child_progress: 0.5, view_child_exams: 0.4, view_child_team: 0.3 },
  ADMIN_COACH: { review_platform: 0.4, review_users: 0.3 },
};

const ROLE_TYPE_BONUS = {
  teacher: { create_team: 2.0, create_exam: 1.5, view_team_activity: 1.0, publish_project: 1.2, respond_requests: 2.0 },
  parent: { view_child_progress: 2.0, view_child_exams: 1.5, view_child_team: 1.2, set_child_profile: 2.0 },
  admin: { review_platform: 1.5, review_users: 1.5 },
  student: {},
};

const estimateDuration = (type) => {
  const map = {
    set_grade: 1, pick_first_book: 3, watch_first_video: 10,
    complete_first_lesson: 15, take_first_quiz: 15, meet_the_app: 2,
    claim_welcome: 1, study: 30, review: 15, finish_lesson: 25,
    watch_video: 20, read_textbook: 45, do_exercise: 30, explore: 30,
    advance_topic: 30, exam: 30, challenge: 15, resume_draft: 20,
    review_mistakes: 20, wait_correction: 5, follow_plan: 30,
    create_plan: 15, streak_check: 2, view_medals: 3, almost_medal: 5,
    celebrate: 2, leaderboard: 3, challenge_friend: 5, join_group: 5,
    mentor_friend: 10, take_break: 15, sleep_reminder: 1, mood_check: 1,
    parent_view: 3, learn_skill: 10, fix_streak: 25, resume_yesterday: 20,
    welcome_back: 10, restart_journey: 15,
    create_team: 10, create_exam: 30, view_team_activity: 5,
    publish_project: 20, respond_requests: 5,
    view_child_progress: 3, view_child_exams: 3, view_child_team: 3,
    set_child_profile: 5, review_platform: 5, review_users: 5,
  };
  return map[type] || 15;
};

const scoreCandidates = (candidates, sig, state) => {
  const modeBonus = MODE_TYPE_BONUS[state.mode] || {};
  const roleBonus = ROLE_TYPE_BONUS[sig.identity.role] || {};
  const recentTypes = safeObject(sig.coach.recentTypes);
  const successRates = safeObject(sig.coach.successRates);

  return safeArray(candidates).map((c) => {
    let score = c.weight || 1.0;

    score += modeBonus[c.type] || 0;
    score += roleBonus[c.type] || 0;

    if (c.urgency) score += c.urgency * 0.15;

    if (c.subject && MAIN_SUBJECTS.includes(c.subject)) score += 0.1;
    if (c.subject && sig.subjects[c.subject]?.category === 'weakness') score += 0.25;
    if (c.subject && sig.subjects[c.subject]?.category === 'strength') score += 0.15;

    const duration = c.duration || estimateDuration(c.type);
    if (sig.time.hour >= 22 && duration > 30) score *= 0.5;
    if (sig.time.hour < 7 && duration > 45) score *= 0.7;

    if (c.subject) {
      const lastUsage = recentTypes[`subject_${c.subject}`] || 0;
      const hoursSince = (Date.now() - lastUsage) / 3600000;
      if (hoursSince < 4) score *= 0.4;
      else if (hoursSince < 12) score *= 0.7;
    }

    const lastType = recentTypes[`type_${c.type}`] || 0;
    const daysSince = (Date.now() - lastType) / 86400000;
    if (daysSince < 0.5) score *= 0.5;

    if (successRates[c.type]?.total >= 2) {
      const rate = successRates[c.type].success / successRates[c.type].total;
      score *= 0.6 + rate * 0.8;
    }

    if (sig.temporal.daysToNearestExam !== null && sig.temporal.daysToNearestExam <= 3) {
      if (['study', 'read_textbook', 'review_mistakes', 'follow_plan'].includes(c.type)) score *= 1.7;
      if (['watch_video', 'explore', 'leaderboard'].includes(c.type)) score *= 0.5;
    }

    const capacity = (sig.study.avg7 || 60) - sig.study.todayMinutes;
    if (duration > capacity + 30) score *= 0.4;

    if (c.type === SUGGESTION_TYPES.FOLLOW_PLAN && sig.planning.adherence7d < 0.3) score *= 0.6;

    if (state.motivation === 'LOW' && ['challenge', 'exam'].includes(c.type)) score *= 0.7;
    if (state.motivation === 'LOW' && ['almost_medal', 'celebrate', 'streak_check'].includes(c.type)) score *= 1.4;

    if (state.overall === USER_STATES.EXAM_WEEK) {
      if (['study', 'read_textbook', 'review_mistakes'].includes(c.type)) score *= 1.5;
      if (['leaderboard', 'challenge_friend', 'view_medals'].includes(c.type)) score *= 0.4;
    }

    if (state.overall === USER_STATES.LOST || state.overall === USER_STATES.BURNT_OUT) {
      if (['take_break', 'sleep_reminder', 'mood_check'].includes(c.type)) score *= 1.6;
      if (['exam', 'challenge'].includes(c.type)) score *= 0.5;
    }

    if (state.overall === USER_STATES.NEW_VISITOR) {
      const onboardingTypes = [
        SUGGESTION_TYPES.SET_GRADE, SUGGESTION_TYPES.PICK_FIRST_BOOK,
        SUGGESTION_TYPES.WATCH_FIRST_VIDEO, SUGGESTION_TYPES.COMPLETE_FIRST_LESSON,
        SUGGESTION_TYPES.TAKE_FIRST_QUIZ, SUGGESTION_TYPES.MEET_THE_APP,
      ];
      if (onboardingTypes.includes(c.type)) score *= 2.5;
      else score *= 0.3;
    }

    if (
      state.overall === USER_STATES.ONBOARDING_1 ||
      state.overall === USER_STATES.ONBOARDING_2 ||
      state.overall === USER_STATES.ONBOARDING_3
    ) {
      if ([
        SUGGESTION_TYPES.PICK_FIRST_BOOK, SUGGESTION_TYPES.WATCH_FIRST_VIDEO,
        SUGGESTION_TYPES.COMPLETE_FIRST_LESSON, SUGGESTION_TYPES.TAKE_FIRST_QUIZ,
        SUGGESTION_TYPES.CREATE_PLAN, SUGGESTION_TYPES.LEADERBOARD,
      ].includes(c.type)) score *= 1.7;
    }

    if (state.overall === USER_STATES.RETURNING || state.overall === USER_STATES.LOST) {
      if ([SUGGESTION_TYPES.WELCOME_BACK, SUGGESTION_TYPES.RESTART_JOURNEY, SUGGESTION_TYPES.RESUME_YESTERDAY].includes(c.type)) {
        score *= 1.8;
      }
    }

    return { ...c, score, estimatedDuration: duration };
  }).sort((a, b) => b.score - a.score);
};

// ============================================================
// SECTION 9 — SELECTION
// ============================================================

const selectDiverse = (scored, budget) => {
  const picked = [];
  const subjectCount = {};
  const typeCount = {};
  const categoryCount = {};

  const typeToCategory = (t) => {
    if (['set_grade', 'pick_first_book', 'watch_first_video', 'complete_first_lesson', 'take_first_quiz', 'meet_the_app', 'claim_welcome'].includes(t)) return 'onboard';
    if (['study', 'review', 'finish_lesson', 'watch_video', 'read_textbook', 'do_exercise', 'explore', 'advance_topic'].includes(t)) return 'edu';
    if (['exam', 'challenge', 'resume_draft', 'review_mistakes'].includes(t)) return 'assess';
    if (['follow_plan', 'create_plan', 'set_goal', 'reschedule'].includes(t)) return 'plan';
    if (['streak_check', 'view_medals', 'celebrate', 'almost_medal'].includes(t)) return 'motiv';
    if (['leaderboard', 'challenge_friend', 'join_group', 'mentor_friend'].includes(t)) return 'social';
    if (['take_break', 'sleep_reminder', 'mood_check'].includes(t)) return 'care';
    if (['welcome_back', 'restart_journey', 'resume_yesterday', 'fix_streak'].includes(t)) return 'return';
    if (['create_team', 'create_exam', 'view_team_activity', 'publish_project', 'respond_requests'].includes(t)) return 'teacher';
    if (['view_child_progress', 'view_child_exams', 'view_child_team', 'set_child_profile'].includes(t)) return 'parent';
    if (['review_platform', 'review_users'].includes(t)) return 'admin';
    return 'other';
  };

  const safeScored = safeArray(scored);

  for (const c of safeScored) {
    if (picked.length >= budget) break;

    const sc = subjectCount[c.subject || '_'] || 0;
    const tc = typeCount[c.type] || 0;
    const cat = typeToCategory(c.type);
    const cc = categoryCount[cat] || 0;

    if (c.subject && sc >= 2) continue;
    if (tc >= 1) continue;
    if (cc >= 3) continue;

    picked.push(c);
    if (c.subject) subjectCount[c.subject] = sc + 1;
    typeCount[c.type] = tc + 1;
    categoryCount[cat] = cc + 1;
  }

  if (picked.length < Math.min(budget, 3)) {
    for (const c of safeScored) {
      if (picked.length >= budget) break;
      if (picked.some((p) => p.type === c.type && p.subject === c.subject)) continue;
      picked.push(c);
    }
  }

  if (!picked.length && safeScored.length) picked.push(safeScored[0]);

  return picked.map((c, i) => ({
    ...c,
    id: `${c.type}_${c.subject || 'any'}_${i}_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  }));
};

// ============================================================
// SECTION 10 — NARRATIVE
// ============================================================

const buildGreeting = (state, sig) => {
  const name = sig.identity.firstName;
  const h = sig.time.hour;
  const timeG = h < 5 ? 'شب بخیر' : h < 12 ? 'صبح بخیر' : h < 18 ? 'وقت بخیر' : 'شب بخیر';

  if (sig.identity.role === 'teacher') {
    return `${timeG} ${name}! کلاس امروزت چطوره؟`;
  }
  if (sig.identity.role === 'parent') {
    return `${timeG} ${name}! بیا پیشرفت فرزندت رو ببینیم`;
  }
  if (sig.identity.role === 'admin') {
    return `${timeG} ${name}! پلتفرم امروز چه خبره؟`;
  }

  const templates = {
    NEW_VISITOR: `${timeG} ${name}! 🎉 خوش اومدی`,
    ONBOARDING_1: `${timeG} ${name}! 🌱 بیا با هم شروع کنیم`,
    ONBOARDING_2: `${timeG} ${name}! ادامه بده، خوب پیش می‌ری`,
    ONBOARDING_3: `سلام ${name}! 🚀 داری اپ رو کشف می‌کنی`,
    RETURNING: `خوش برگشتی ${name}! 👋`,
    LOST: `دوباره سلام ${name}! 💚 برگشتی، وقتشه`,
    BURNT_OUT: `${name} جان، یه روز سبک‌تر داشته باشیم`,
    STRUGGLING: `${name} جان، یه قدم کوچیک با هم`,
    EXCELLING: `واو ${name}! 🚀 داری می‌درخشی`,
    BUILDING: `${name} جان، روندت عالیه`,
    STEADY: `${name} جان، ثباتت تحسینه`,
    EXAM_WEEK: `${name} جان! 📚 امتحان نزدیکه، بزن بریم`,
    DEADLINE_PRESSURE: `${name} جان، بیا اولویت‌بندی کنیم`,
    PLATEAU: `${name} جان، بیا یه تغییر بذاریم`,
  };
  return templates[state.overall] || `${timeG} ${name}!`;
};

const buildAnalysis = (sig) => {
  const strengths = Object.values(sig.subjects)
    .filter((s) => s.category === 'strength')
    .slice(0, 3)
    .map((s) => ({
      subject: s.subject,
      title: s.title,
      reason: s.scoreAvg !== null ? `${toFa(s.scoreAvg)}٪` : `${toFa(s.progress)}٪`,
      evidence: `${toFa(s.minutes30d)} دقیقه`,
    }));

  const weaknesses = Object.values(sig.subjects)
    .filter((s) => s.category === 'weakness' || s.category === 'neglected')
    .slice(0, 3)
    .map((s) => ({
      subject: s.subject,
      title: s.title,
      reason: s.category === 'neglected'
        ? `${toFa(s.recencyDays)} روز غفلت`
        : `${toFa(s.scoreAvg ?? s.progress)}٪`,
      evidence: `پیشرفت ${toFa(s.progress)}٪`,
    }));

  const alerts = [];
  if (sig.temporal.draftCount > 0) alerts.push(`${toFa(sig.temporal.draftCount)} آزمون نیمه‌کاره`);
  if (sig.exams.pendingCorrections > 0) alerts.push(`${toFa(sig.exams.pendingCorrections)} تصحیح معلق`);

  const opportunities = [];
  if (sig.motivation.almostMedalsCount > 0) opportunities.push(`${toFa(sig.motivation.almostMedalsCount)} مدال نزدیک`);

  return { strengths, weaknesses, alerts, opportunities };
};

const buildTrend = (sig) => {
  if (sig.meta.realDataScore === 0) return { key: 'new', label: 'شروع تازه', emoji: '🌱' };
  const t = sig.study.trend7vs7prev;
  if (t > 1.3 && sig.study.total7prev > 30) {
    return { key: 'up', label: `صعودی ${toFa(Math.round((t - 1) * 100))}٪`, emoji: '📈' };
  }
  if (t < 0.7 && sig.study.total7prev > 30) {
    return { key: 'down', label: `نزولی ${toFa(Math.round((1 - t) * 100))}٪`, emoji: '📉' };
  }
  return { key: 'stable', label: 'پایدار', emoji: '➡️' };
};

// ============================================================
// SECTION 11 — AI ENRICHMENT
// ============================================================

const AI_CONFIG = {
  proxyUrl: process.env.REACT_APP_AI_COACH_PROXY || '',
  timeoutMs: 12000,
};

const enrichWithAI = async (plan, sig) => {
  if (!AI_CONFIG.proxyUrl) return plan;
  try {
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), AI_CONFIG.timeoutMs);
    const res = await fetch(AI_CONFIG.proxyUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt: buildAIPrompt(plan, sig),
        context: { state: plan.meta.state, mode: plan.meta.mode },
      }),
      signal: controller.signal,
    });
    clearTimeout(tid);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const parsed = await res.json();
    if (parsed.greeting && typeof parsed.greeting === 'string') plan.greeting.text = parsed.greeting;
    if (Array.isArray(parsed.suggestionWhys)) {
      plan.suggestions.forEach((s, i) => {
        if (parsed.suggestionWhys[i]) s.why = parsed.suggestionWhys[i];
      });
    }
    plan.meta.source = 'ai';
    return plan;
  } catch {
    return plan;
  }
};

const buildAIPrompt = (plan, sig) => {
  return `تو یک مشاور آموزشی ایرانی هستی. متنی کوتاه، صمیمی و بدون اضافه‌گویی بنویس.

نام: ${sig.identity.name}
نقش: ${sig.identity.role}
پایه: ${sig.identity.gradeRaw || 'نامشخص'}
حالت: ${plan.meta.state}
پیشنهادها: ${safeArray(plan.suggestions).map(s => `${s.title} (${s.type})`).join(' | ')}

خروجی JSON:
{
  "greeting": "پیام گرم یک جمله‌ای",
  "suggestionWhys": ["دلیل هر پیشنهاد، حداکثر ۸ کلمه"]
}`;
};

// ============================================================
// SECTION 12 — CACHE & MEMORY
// ============================================================

const CACHE_PFX = 'aiCoachPlan_';
const PROGRESS_PFX = 'aiCoachProgress_';
const FEEDBACK_PFX = 'aiCoach_feedback_';

const cacheKey = (userId, grade) =>
  `${CACHE_PFX}${userId}_${grade || 'nograde'}_${localTodayStr()}`;
const progressKey = (userId) => `${PROGRESS_PFX}${userId}_${localTodayStr()}`;
const feedbackKey = (userId) => `${FEEDBACK_PFX}${userId}`;

const sanitizePlan = (plan) => {
  if (!plan || typeof plan !== 'object') return null;
  if (!plan.meta || typeof plan.meta !== 'object') plan.meta = {};
  if (!plan.analysis || typeof plan.analysis !== 'object') plan.analysis = {};
  if (!Array.isArray(plan.analysis.strengths)) plan.analysis.strengths = [];
  if (!Array.isArray(plan.analysis.weaknesses)) plan.analysis.weaknesses = [];
  if (!Array.isArray(plan.analysis.alerts)) plan.analysis.alerts = [];
  if (!Array.isArray(plan.analysis.opportunities)) plan.analysis.opportunities = [];
  if (!Array.isArray(plan.patterns)) plan.patterns = [];
  if (!Array.isArray(plan.suggestions)) plan.suggestions = [];

  plan.suggestions = plan.suggestions
    .filter((s) => s && typeof s === 'object' && !REMOVED_TYPES.has(s.type))
    .map((s, i) => ({
      ...s,
      id: s.id || `${s.type || 'sug'}_${i}_${Date.now()}`,
    }));

  return plan;
};

const readCache = (userId, grade) => {
  try {
    const key = cacheKey(userId, grade);
    const cached = safeJSON(localStorage.getItem(key), null);
    if (!cached) return null;
    if (cached.meta?.version !== CACHE_VERSION) {
      try { localStorage.removeItem(key); } catch {}
      return null;
    }
    return sanitizePlan(cached);
  } catch {
    return null;
  }
};

export const getCachedPlan = (userId, grade) => readCache(userId, grade);  // 🆕

const writeCache = (userId, grade, plan) => {
  try {
    const safe = sanitizePlan(plan);
    if (!safe) return;
    if (!safe.meta) safe.meta = {};
    safe.meta.version = CACHE_VERSION;
    localStorage.setItem(cacheKey(userId, grade), JSON.stringify(safe));
  } catch {}
};

export const getStepProgress = (userId) => {
  if (!userId) return {};
  return safeObject(readLS(progressKey(userId), {}));
};

export const markStepDone = (userId, stepId) => {
  if (!userId || !stepId) return {};
  const cur = getStepProgress(userId);
  cur[stepId] = true;
  try { localStorage.setItem(progressKey(userId), JSON.stringify(cur)); } catch {}
  return cur;
};

export const recordFeedback = (userId, suggestion, action, extra = {}) => {
  if (!userId || !suggestion) return;
  const fb = safeObject(readLS(feedbackKey(userId), { events: [], successRates: {}, recentTypes: {} }));
  fb.events = safeArray(fb.events).slice(-200);
  fb.events.push({
    type: suggestion.type,
    subject: suggestion.subject || null,
    action,
    timestamp: Date.now(),
    ...extra,
  });
  const t = suggestion.type;
  fb.successRates = safeObject(fb.successRates);
  fb.successRates[t] = fb.successRates[t] || { success: 0, total: 0 };
  fb.successRates[t].total += 1;
  if (action === 'completed') fb.successRates[t].success += 1;
  fb.recentTypes = safeObject(fb.recentTypes);
  fb.recentTypes[`type_${t}`] = Date.now();
  if (suggestion.subject) fb.recentTypes[`subject_${suggestion.subject}`] = Date.now();
  try { localStorage.setItem(feedbackKey(userId), JSON.stringify(fb)); } catch {}
};

const readSuccessRates = (userId) => {
  const fb = safeObject(readLS(feedbackKey(userId), { successRates: {} }));
  return safeObject(fb.successRates);
};

export const invalidateCache = (userId, grade) => {
  if (!userId) return;
  try {
    if (grade !== undefined) localStorage.removeItem(cacheKey(userId, grade));
    // fallback: پاک کردن همه‌ی کلیدهای این کاربر
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (k?.startsWith(`${CACHE_PFX}${userId}_`)) localStorage.removeItem(k);
    }
  } catch {}
};

// ============================================================
// SECTION 13 — ACTION BUILDER
// ============================================================

export const buildAction = (suggestion, navigate) => {
  const { type, subject, examId } = suggestion || {};
  const grade = suggestion?._grade;
  const safeNavigate = typeof navigate === 'function' ? navigate : () => {};

  return () => {
    try {
      switch (type) {
        case SUGGESTION_TYPES.SET_GRADE:
          safeNavigate('/challenge');
          break;
        case SUGGESTION_TYPES.PICK_FIRST_BOOK:
          safeNavigate('/books');
          break;
        case SUGGESTION_TYPES.WATCH_FIRST_VIDEO:
          safeNavigate('/books');
          break;
        case SUGGESTION_TYPES.COMPLETE_FIRST_LESSON:
          if (grade && subject) safeNavigate(`/course/${grade}/${subject}`);
          else safeNavigate('/books');
          break;
        case SUGGESTION_TYPES.TAKE_FIRST_QUIZ:
          safeNavigate('/exams/student');
          break;
        case SUGGESTION_TYPES.MEET_THE_APP:
          safeNavigate('/home');
          break;
        case SUGGESTION_TYPES.CLAIM_WELCOME:
          safeNavigate('/home');
          break;
        case SUGGESTION_TYPES.STUDY:
        case SUGGESTION_TYPES.EXPLORE:
        case SUGGESTION_TYPES.FINISH_LESSON:
        case SUGGESTION_TYPES.ADVANCE_TOPIC:
          if (grade && subject) safeNavigate(`/course/${grade}/${subject}`);
          else safeNavigate('/books');
          break;
        case SUGGESTION_TYPES.WATCH_VIDEO:
        case SUGGESTION_TYPES.READ_TEXTBOOK:
          safeNavigate('/books');
          break;
        case SUGGESTION_TYPES.DO_EXERCISE:
        case SUGGESTION_TYPES.LEARN_SKILL:
          safeNavigate('/StudyTimer');
          break;
        case SUGGESTION_TYPES.REVIEW:
        case SUGGESTION_TYPES.REVIEW_MISTAKES:
          safeNavigate(`/StudyTimer?mode=review&subject=${subject || ''}`);
          break;
        case SUGGESTION_TYPES.EXAM:
        case SUGGESTION_TYPES.CHALLENGE:
          if (examId) safeNavigate(`/exams/take/${examId}`);
          else safeNavigate('/exams/student');
          break;
        case SUGGESTION_TYPES.RESUME_DRAFT:
        case SUGGESTION_TYPES.WAIT_CORRECTION:
          safeNavigate('/exams/student');
          break;
        case SUGGESTION_TYPES.FOLLOW_PLAN:
        case SUGGESTION_TYPES.CREATE_PLAN:
        case SUGGESTION_TYPES.RESCHEDULE:
        case SUGGESTION_TYPES.SET_GOAL:
          safeNavigate('/Lesson-planning');
          break;
        case SUGGESTION_TYPES.STREAK_CHECK:
        case SUGGESTION_TYPES.CHALLENGE_FRIEND:
          safeNavigate('/challenge');
          break;
        case SUGGESTION_TYPES.LEADERBOARD:
          safeNavigate('/medals');
          break;
        case SUGGESTION_TYPES.VIEW_MEDALS:
        case SUGGESTION_TYPES.ALMOST_MEDAL:
        case SUGGESTION_TYPES.CELEBRATE:
          safeNavigate('/medals');
          break;
        case SUGGESTION_TYPES.JOIN_GROUP:
        case SUGGESTION_TYPES.MENTOR_FRIEND:
          safeNavigate('/team-dashboard');
          break;
        case SUGGESTION_TYPES.TAKE_BREAK:
        case SUGGESTION_TYPES.SLEEP_REMINDER:
        case SUGGESTION_TYPES.FIX_STREAK:
        case SUGGESTION_TYPES.RESUME_YESTERDAY:
        case SUGGESTION_TYPES.WELCOME_BACK:
        case SUGGESTION_TYPES.RESTART_JOURNEY:
          safeNavigate('/StudyTimer');
          break;
        case SUGGESTION_TYPES.MOOD_CHECK:
          safeNavigate('/home');
          break;
        case SUGGESTION_TYPES.PARENT_VIEW:
          safeNavigate('/parent-dashboard');
          break;
        case SUGGESTION_TYPES.CREATE_TEAM:
        case SUGGESTION_TYPES.VIEW_TEAM_ACTIVITY:
        case SUGGESTION_TYPES.RESPOND_REQUESTS:
          safeNavigate('/team-dashboard');
          break;
        case SUGGESTION_TYPES.CREATE_EXAM:
          safeNavigate('/exams/create');
          break;
        case SUGGESTION_TYPES.PUBLISH_PROJECT:
          safeNavigate('/teacher/books');
          break;
        case SUGGESTION_TYPES.VIEW_CHILD_PROGRESS:
        case SUGGESTION_TYPES.VIEW_CHILD_EXAMS:
        case SUGGESTION_TYPES.VIEW_CHILD_TEAM:
        case SUGGESTION_TYPES.SET_CHILD_PROFILE:
          safeNavigate('/parent-dashboard');
          break;
        case SUGGESTION_TYPES.REVIEW_PLATFORM:
        case SUGGESTION_TYPES.REVIEW_USERS:
          safeNavigate('/AdminHome');
          break;
        default:
          safeNavigate('/home');
      }
    } catch (e) {
      console.warn('[aiCoach] buildAction failed:', e);
    }
  };
};

// ============================================================
// SECTION 14 — SNAPSHOT & PENDING
// ============================================================

const PENDING_KEY = (userId) => `aiCoach_pending_${userId}`;
const VISITS_KEY = (userId) => `aiCoach_visits_${userId}`;
const PENDING_TTL_MS = 8 * 3600 * 1000;
const MIN_WAIT_MS = 1500;

const loadPending = (userId) => {
  try { return safeJSON(localStorage.getItem(PENDING_KEY(userId)), {}) || {}; }
  catch { return {}; }
};
const savePending = (userId, pending) => {
  try { localStorage.setItem(PENDING_KEY(userId), JSON.stringify(pending)); } catch {}
};
const loadVisits = (userId) => {
  try { return safeJSON(localStorage.getItem(VISITS_KEY(userId)), []) || []; }
  catch { return []; }
};

export const takeSnapshot = (userId, exams) => {
  try {
    const user = safeObject(readLS('user', {}));
    const data = readUnifiedData(userId, user, exams);
    if (!data) throw new Error('no data');

    const dailyTotal = Object.values(data.study.daily).reduce((a, b) => a + (Number(b) || 0), 0);

    return {
      dailyTotal,
      bookStats: { ...data.study.bookStats },
      lessonProgressCount: data.lessons.lessonProgressCount,
      lessonProgressBySubject: { ...data.lessons.lessonProgressBySubject },
      lessonRecordsCount: data.lessons.lessonRecordsCount,
      examResults: { ...data.exams.examResults },
      completedChallenges: data.exams.completedChallenges,
      streak: data.study.streak,
      streakLastDate: data.study.streakLastDate,
      lastActiveDays: data.study.lastActiveDays,
      grade: data.identity.grade,
      field: data.identity.field,
      role: data.identity.role,
      plansCount: data.planner.plansCount,
      latestPlanCreatedAt: data.planner.latestPlan?.createdAt || null,
      todayPlanDone: data.planner.todayPlan?.done || 0,
      todayPlanTotal: data.planner.todayPlan?.total || 0,
      activeTeamMembers: data.team.activeTeamMembers,
      pendingTeamMembers: data.team.pendingTeamMembers,
      isInTeam: data.team.isInTeam,
      achievementsUnlocked: data.achievements.unlockedCount,
      almostMedalsCount: data.achievements.almostCount,
      watchedVideosCount: data.video.count,
      moodToday: data.mood.hasMoodToday,
    };
  } catch (e) {
    console.warn('[takeSnapshot] fallback:', e);
    return {
      dailyTotal: 0, bookStats: {}, lessonProgressCount: 0,
      lessonProgressBySubject: {}, lessonRecordsCount: 0,
      examResults: {}, completedChallenges: 0,
      streak: 0, streakLastDate: null, lastActiveDays: 999,
      grade: null, field: null, role: 'student',
      plansCount: 0, latestPlanCreatedAt: null,
      todayPlanDone: 0, todayPlanTotal: 0,
      activeTeamMembers: 0, pendingTeamMembers: 0, isInTeam: false,
      achievementsUnlocked: 0, almostMedalsCount: 0,
      watchedVideosCount: 0, moodToday: false,
    };
  }
};

export const buildPendingItem = (sug, snapshot = {}) => ({
  id: sug.id,
  type: sug.type,
  subject: sug.subject || null,
  examId: sug.examId || null,
  grade: sug._grade || null,
  title: sug.title || '',
  clickTime: Date.now(),
  snapshot,
});

const repairPendingItems = (userId, currentSnapshot) => {
  const pending = loadPending(userId);
  const now = Date.now();
  let changed = false;
  for (const [id, item] of Object.entries(pending)) {
    if (!item) {
      delete pending[id];
      changed = true;
      continue;
    }
    if (!item.clickTime || now - item.clickTime > PENDING_TTL_MS) {
      delete pending[id];
      changed = true;
      continue;
    }
    if (REMOVED_TYPES.has(item.type)) {
      delete pending[id];
      changed = true;
      continue;
    }
    if (!item.snapshot || typeof item.snapshot !== 'object') {
      item.snapshot = currentSnapshot || takeSnapshot(userId, []);
      changed = true;
    }
  }
  if (changed) savePending(userId, pending);
  return changed;
};

// ============================================================
// SECTION 15 — COMPLETION RULES
// ============================================================

export const COMPLETION_RULES = {
  // آموزشی
  study: (item, snap, ctx) => {
    if (ctx.dailyTotal - snap.dailyTotal >= 3) return true;
    if (item.subject) {
      const bookDelta = (ctx.bookStats[item.subject] || 0) - (snap.bookStats[item.subject] || 0);
      if (bookDelta >= 3) return true;
      const lessonDelta = (ctx.lessonProgressBySubject[item.subject] || 0) -
        (snap.lessonProgressBySubject[item.subject] || 0);
      if (lessonDelta > 0) return true;
    }
    return ctx.lessonProgressCount - snap.lessonProgressCount > 0;
  },
  review: (item, snap, ctx) => {
    if (ctx.dailyTotal - snap.dailyTotal >= 3) return true;
    if (item.subject) {
      const bookDelta = (ctx.bookStats[item.subject] || 0) - (snap.bookStats[item.subject] || 0);
      if (bookDelta >= 3) return true;
    }
    return false;
  },
  finish_lesson: (item, snap, ctx) => {
    if (ctx.lessonProgressCount - snap.lessonProgressCount > 0) return true;
    if (item.subject) {
      const lessonDelta = (ctx.lessonProgressBySubject[item.subject] || 0) -
        (snap.lessonProgressBySubject[item.subject] || 0);
      if (lessonDelta > 0) return true;
    }
    return false;
  },
  read_textbook: (item, snap, ctx) => (ctx.dailyTotal - snap.dailyTotal) >= 5,
  do_exercise: (item, snap, ctx) => (ctx.dailyTotal - snap.dailyTotal) >= 3,
  explore: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 3 ||
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,
  advance_topic: (item, snap, ctx) =>
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,
  watch_video: (item, snap, ctx) =>
    (ctx.watchedVideosCount - snap.watchedVideosCount) > 0,

  // آزمون
  exam: (item, snap, ctx) => {
    if (!item.examId) return false;
    return !snap.examResults[item.examId] && ctx.examResults[item.examId] === true;
  },
  challenge: (item, snap, ctx) => {
    if (!item.examId) return false;
    return !snap.examResults[item.examId] && ctx.examResults[item.examId] === true;
  },
  resume_draft: (item, snap, ctx) =>
    Object.keys(ctx.examResults).some((id) => ctx.examResults[id] && !snap.examResults[id]),
  review_mistakes: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 5 ||
    Object.keys(ctx.examResults).some((id) => ctx.examResults[id] && !snap.examResults[id]),

  // برنامه‌ریزی
  create_plan: (item, snap, ctx) => ctx.plansCount > snap.plansCount,
  follow_plan: (item, snap, ctx) => ctx.todayPlanDone > snap.todayPlanDone,
  set_goal: (item, snap, ctx) =>
    ctx.plansCount > snap.plansCount || ctx.todayPlanTotal > snap.todayPlanTotal,
  reschedule: (item, snap, ctx) => ctx.latestPlanCreatedAt !== snap.latestPlanCreatedAt,

  // انگیزشی
  streak_check: (item, snap, ctx) => {
    const t = localTodayStr();
    return ctx.streakLastDate === t && snap.streakLastDate !== t;
  },
  view_medals: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/medals' || v.path?.startsWith('/medals')),
  celebrate: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/medals'),
  almost_medal: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/medals') ||
    ctx.almostMedalsCount > snap.almostMedalsCount ||
    ctx.achievementsUnlocked > snap.achievementsUnlocked,

  // اجتماعی
  leaderboard: (item, snap, ctx, visited) =>
    visited.some((v) =>
      v.path === '/challenge' || v.path?.startsWith('/challenge') ||
      v.path === '/medals' || v.path?.startsWith('/medals')
    ),
  challenge_friend: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/challenge' || v.path?.startsWith('/challenge')),
  join_group: (item, snap, ctx, visited) =>
    ctx.activeTeamMembers > snap.activeTeamMembers ||
    ctx.pendingTeamMembers > snap.pendingTeamMembers ||
    visited.some((v) => v.path === '/team-dashboard'),
  mentor_friend: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/team-dashboard'),

  // خودمراقبتی
  mood_check: (item, snap, ctx, visited) =>
    (!snap.moodToday && ctx.moodToday) ||
    visited.some((v) =>
      v.path === '/home' || v.path === '/StudentHome' ||
      v.path === '/TeacherHome' || v.path === '/ParentHome' || v.path === '/AdminHome'
    ),
  take_break: () => true,
  sleep_reminder: () => true,

  learn_skill: (item, snap, ctx) => (ctx.dailyTotal - snap.dailyTotal) >= 3,

  // رشدی
  parent_view: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/parent-dashboard' || v.path?.startsWith('/parent-dashboard')),
  wait_correction: () => true,

  // آنبوردینگ
  set_grade: (item, snap, ctx) => !!ctx.grade && ctx.grade !== snap.grade,
  pick_first_book: (item, snap, ctx) =>
    ctx.lessonRecordsCount > snap.lessonRecordsCount ||
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,
    watch_first_video: (item, snap, ctx) =>
    ctx.watchedVideosCount > snap.watchedVideosCount,
  complete_first_lesson: (item, snap, ctx) =>
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,
  take_first_quiz: (item, snap, ctx) =>
    Object.keys(ctx.examResults).some((id) => ctx.examResults[id]),
  meet_the_app: (item, snap, ctx, visited) =>
    visited.some((v) =>
      v.path === '/home' ||
      v.path === '/StudentHome' ||
      v.path === '/TeacherHome' ||
      v.path === '/ParentHome' ||
      v.path === '/AdminHome'
    ),
  claim_welcome: () => true,

  // سیستمی — بازگشت
  fix_streak: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 3,
  resume_yesterday: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 3,
  welcome_back: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 3 ||
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,
  restart_journey: (item, snap, ctx) =>
    (ctx.dailyTotal - snap.dailyTotal) >= 3 ||
    (ctx.lessonProgressCount - snap.lessonProgressCount) > 0,

  // معلم
  create_team: (item, snap, ctx) =>
    ctx.activeTeamMembers > snap.activeTeamMembers,
  create_exam: (item, snap, ctx, visited) =>
    visited.some((v) => v.path?.startsWith('/exams/create')) ||
    (ctx.dailyTotal - snap.dailyTotal) >= 1,
  view_team_activity: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/team-dashboard'),
  publish_project: (item, snap, ctx, visited) =>
    visited.some((v) => v.path?.startsWith('/teacher/books')),
  respond_requests: (item, snap, ctx, visited) =>
    ctx.pendingTeamMembers < snap.pendingTeamMembers ||
    ctx.activeTeamMembers > snap.activeTeamMembers ||
    visited.some((v) => v.path === '/team-dashboard'),

  // والدین
  view_child_progress: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/parent-dashboard' || v.path?.startsWith('/parent-dashboard')),
  view_child_exams: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/parent-dashboard' || v.path?.startsWith('/parent-dashboard')),
  view_child_team: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/parent-dashboard' || v.path?.startsWith('/parent-dashboard')),
  set_child_profile: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/parent-dashboard' || v.path?.startsWith('/parent-dashboard')),

  // ادمین
  review_platform: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/AdminHome' || v.path?.startsWith('/AdminHome')),
  review_users: (item, snap, ctx, visited) =>
    visited.some((v) => v.path === '/AdminHome' || v.path?.startsWith('/AdminHome')),

  // پیش‌فرض
  _default: (item, snap, ctx, visited) => safeArray(visited).length > 0,
};

// ============================================================
// SECTION 16 — PUBLIC API
// ============================================================

export const getDailyPlan = async (userId, userGrade, user, exams, options = {}) => {
  if (!userId || userId === 'unknown') return null;

  try {
    const role = normalizeRole(user?.role);
    const grade = normalizeGrade(user?.grade || userGrade || readLS(`user-grade-${userId}`));

    // ۱. چک کش
    if (!options.forceRefresh) {
      const cached = readCache(userId, grade);
      if (cached) return cached;
    }

    // ۲. خواندن داده
    const rawData = readUnifiedData(userId, user, exams);
    if (!rawData) return null;

    // ۳. استخراج سیگنال
    const signals = extractSignals(rawData);
    if (!signals) return null;

    // ۴. تشخیص الگو
    const patterns = detectPatterns(signals);

    // ۵. تعیین حالت
    const state = classifyState(signals, patterns);
    const budget = MODE_BUDGET[state.mode] || 4;

    // ۶. تولید کاندیدها
    const candidates = generateCandidates(signals, state);

    // ۷. امتیازدهی
    const scored = scoreCandidates(candidates, signals, state);

    // ۸. انتخاب متنوع
    const selected = selectDiverse(scored, budget);

    // ۹. ساخت تحلیل و روند
    const analysis = buildAnalysis(signals);
    const trend = buildTrend(signals);

    // ۱۰. ساخت پلن نهایی
    const plan = {
      meta: {
        date: localTodayStr(),
        state: state.overall,
        mode: state.mode,
        role,
        budget,
        confidence: signals.meta.confidence,
        urgency: state.urgency,
        source: 'local',
        version: CACHE_VERSION,
        generatedAt: Date.now(),
        onboardingLevel: signals.onboarding.level,
      },
      greeting: {
        text: buildGreeting(state, signals),
        emoji:
          state.mode === ADVISOR_MODES.CHALLENGER ? '🔥' :
          state.mode === ADVISOR_MODES.GUIDE ? '🌟' :
          state.mode === ADVISOR_MODES.TEACHER_COACH ? '👨‍🏫' :
          state.mode === ADVISOR_MODES.PARENT_SUPPORTER ? '👨‍👩‍👧' :
          state.mode === ADVISOR_MODES.ADMIN_COACH ? '⚙️' : '🧠',
      },
      analysis,
      patterns: patterns.slice(0, 5),
      trend,
      state: {
        overall: state.overall,
        motivation: state.motivation,
        temporal: state.temporal,
      },
      suggestions: selected.map((s) => ({
        ...s,
        _grade: signals.identity.grade,
        _field: signals.identity.field,
        why: s.why || s.reason,
      })),
    };

    // ۱۱. غنی‌سازی با AI (اختیاری)
    const finalPlan = await enrichWithAI(plan, signals);

    // ۱۲. sanitize + ذخیره
    const safe = sanitizePlan(finalPlan);
    writeCache(userId, grade, safe);

    return safe;
  } catch (e) {
    console.error('[aiCoach] getDailyPlan error:', e);
    return null;
  }
};

// ============================================================
// SECTION 17 — PENDING REPAIR HELPER (برای استفاده در AiDailyCoachCard)
// ============================================================

export const repairPendingOnMount = (userId, exams) => {
  if (!userId) return;
  try {
    const snapshot = takeSnapshot(userId, exams);
    repairPendingItems(userId, snapshot);
  } catch (e) {
    console.warn('[aiCoach] repairPendingOnMount failed:', e);
  }
};

// ============================================================
// SECTION 18 — EXPORTS
// ============================================================

export { readSuccessRates };

export default {
  getDailyPlan,
  buildAction,
  getStepProgress,
  markStepDone,
  recordFeedback,
  invalidateCache,
  takeSnapshot,
  buildPendingItem,
  repairPendingOnMount,
  readSuccessRates,
  ADVISOR_MODES,
  SUGGESTION_TYPES,
  USER_STATES,
  CACHE_VERSION,
};